import { upsertProgressTimeline } from '@/lib/research-assistant/progress-timeline';
import {
  parseResearchResponsePhase,
  type ResearchResponsePhase,
} from '@/lib/research-assistant/response-phase';
import { resumableGatewayResponse } from '@/lib/agent-gateway/replay-client';
import { useState, useCallback, useEffect, useRef } from 'react';
import { VOICE_CONVERSATION_CHANGED_EVENT } from '@/lib/voice/shared';
import { toast } from 'sonner';
import { requestPreviewCapture } from '@/lib/preview/capture-client';
import {
  PREVIEW_CAPTURE_TIMEOUT_MS,
  type PreviewCaptureRequest,
  type PreviewCaptureResult,
} from '@/lib/preview/capture';
import type {
  Message,
  ScheduledCommand,
  ToolStatus,
  SourceReference,
  ContextUsage,
  ImageAttachment,
  DocumentAttachment,
  TraceTimelineItem,
} from '@/app/app/research-assistant/types';
import { getSourceDeduplicationKey } from '@/lib/research-assistant/types';
import {
  getStoredResearchReasoningEffort,
  resolveResearchReasoningEffort,
  type ResearchReasoningEffort,
} from '@/lib/research-assistant/reasoning-effort';
import { finalizePendingToolStatus } from '@/lib/research-assistant/tool-status';
import type { AgentSkillUsage, SelectedAgentSkill } from '@/lib/agent-skills/types';
import type {
  ResearchExecutionMode,
  ResearchRunAction,
  ResearchRunReceipt,
  ResearchRunStatus,
} from '@/lib/research-assistant/runtime/run-controller';
import { deleteUploadedDocument } from '@/utils/document-utils';
import type {
  GitHubPreviewBrowserContextSnapshot,
  GitHubPreviewContextReference,
} from '@/lib/github/preview/browser-context-shared';
import {
  CANVAS_LIVE_EDIT_STARTED_EVENT,
  type CanvasContextReference,
  type CanvasContextSnapshot,
} from '@/lib/canvas/shared';
import { PROJECT_DESIGN_SYSTEM_UPDATED_EVENT } from '@/lib/project-design-system/shared';
import {
  canCancelSubagent,
  cancelActiveSubagentStatuses,
  isChildExecutionEvent,
  reconcileSubagentStatuses,
  subagentApprovalRequest,
  subagentCancelRequest,
  upsertSubagentStatus,
  type SubagentStatusRow,
} from '@/lib/research-assistant/subagent-status';

interface UseResearchChatProps {
  sessionId: string | null;
  runtimeOwnerKey?: string | null;
  messages: Message[];
  apiCall: (body: Record<string, unknown>, init?: RequestInit) => Promise<Response>;
  selectedModelId?: string;
  reasoningEffortModelIds?: string[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setSessionUpdatedAt: (date: string) => void;
  setContextUsage: (usage: ContextUsage | null) => void;
  setSessionName: React.Dispatch<React.SetStateAction<string | null>>;
  getActiveReportId?: () => string | null;
  getWebsitePreviewContextSnapshot?: () => GitHubPreviewBrowserContextSnapshot | null;
  getWebsitePreviewContextReference?: () => GitHubPreviewContextReference | null;
  clearWebsitePreviewSelection?: (expectedContextVersion: number) => Promise<void>;
  getCanvasContextSnapshot?: () => CanvasContextSnapshot | null;
  getCanvasContextReference?: () => CanvasContextReference | null;
  clearCanvasSelection?: (expectedContextVersion: number) => void;
  canvasEditingActive?: boolean;
  designSystemEditingActive?: boolean;
}

interface UseResearchChatReturn {
  // State
  isLoading: boolean;
  streamingText: string;
  thinkingText: string;
  responsePhase: ResearchResponsePhase | null;
  streamTimeline: TraceTimelineItem[];
  activeTools: ToolStatus[];
  subagents: SubagentStatusRow[];
  runStatus: ResearchRunStatus | null;
  activeSessionIds: string[];
  unreadSessionIds: string[];
  scheduledCommands: ScheduledCommand[];
  activeTurnId: string | undefined;
  activeTurnSelectedSkills: SelectedAgentSkill[] | undefined;
  activeTurnExecutionMode: ResearchExecutionMode | undefined;
  activeTurnModelId: string | undefined;
  activeTurnReasoningEffort: ResearchReasoningEffort | null | undefined;
  activeTurnActiveReportId: string | null | undefined;
  latestAppliedSteerReceipt: AppliedSteerReceipt | null;
  inputMessage: string;
  setInputMessage: (msg: string) => void;

  // Actions
  sendMessage: () => Promise<void>;
  sendMessageDirect: (
    content: string,
    images?: ImageAttachment[],
    selectedSkills?: SelectedAgentSkill[],
    executionMode?: ResearchExecutionMode,
    documents?: DocumentAttachment[],
    restoreDocumentsOnReject?: boolean,
    context?: DirectMessageContext
  ) => Promise<boolean>;
  steerMessageDirect: (
    content: string,
    images?: ImageAttachment[],
    selectedSkills?: SelectedAgentSkill[],
    documents?: DocumentAttachment[],
    options?: DirectSteerOptions
  ) => Promise<SteerSubmissionResult>;
  steerScheduledCommand: (scheduledCommandId: string) => Promise<void>;
  continueRun: (
    runStatus: ResearchRunStatus,
    action: Exclude<ResearchRunAction, 'cancel'>,
    message: string
  ) => Promise<void>;
  cancelRun: (runStatus: ResearchRunStatus) => Promise<void>;
  cancelSubagent: (agentId: string) => Promise<void>;
  reviewSubagentApproval: (agentId: string, decision: 'approved' | 'rejected') => Promise<void>;
  cancelScheduledCommand: (scheduledCommandId: string) => void;
  clearScheduledCommands: () => void;
  stopCurrentResponse: () => void;
  retryMessage: (modelMessageId: string) => Promise<void>;
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

const TIMELINE_FLUSH_INTERVAL_MS = 150;
const STREAM_UI_FLUSH_INTERVAL_MS = 32;
const STREAM_RECOVERY_TIMEOUT_MS = 6 * 60 * 1000;
const STREAM_RECOVERY_POLL_MS = 4_000;
const SERVER_STREAM_ERROR_NAME = 'ResearchStreamServerError';

type SessionRecoveryResponse = {
  session?: {
    messages?: Message[];
    updatedAt?: string;
    contextUsage?: ContextUsage | null;
  };
  runStatus?: ResearchRunStatus;
};

type InFlightTurn = {
  sessionId: string;
  requestController: AbortController;
  reader: ReadableStreamDefaultReader<Uint8Array> | null;
  steeringReady: boolean;
  optimisticMessage: Message;
  baselineMessageCount: number;
  activeReportSelectionBaseline: string | null;
  stoppedSnapshot?: StreamSnapshot;
};

type SendMessageOptions = {
  /** Subscribe to an already accepted turn; never resubmit its input. */
  resumeGatewayTurn?: boolean;
  resumeImageAttachments?: Message['imageAttachments'];
  onAccepted?: () => void;
  content: string;
  images?: ImageAttachment[];
  documents?: DocumentAttachment[];
  selectedSkills?: SelectedAgentSkill[];
  targetSessionId?: string;
  targetModelId?: string;
  targetActiveReportId?: string | null;
  executionMode?: ResearchExecutionMode;
  turnId?: string;
  runId?: string;
  runAction?: Exclude<ResearchRunAction, 'cancel'>;
  expectedSequence?: number;
  reasoningEffort?: ResearchReasoningEffort;
  restoreDocumentsOnReject?: boolean;
  suppressPreStreamFailureTurn?: boolean;
  websitePreviewContext?: GitHubPreviewBrowserContextSnapshot;
  websitePreviewContextRef?: GitHubPreviewContextReference;
  canvasContext?: CanvasContextSnapshot;
  canvasContextRef?: CanvasContextReference;
};

type SteerMessageOptions = {
  content: string;
  images?: ImageAttachment[];
  documents?: DocumentAttachment[];
  selectedSkills?: SelectedAgentSkill[];
  targetSessionId?: string;
  targetTurnId?: string;
  targetModelId?: string;
  targetActiveReportId?: string | null;
  executionMode?: ResearchExecutionMode;
  reasoningEffort?: ResearchReasoningEffort | null;
  steerId: string;
  websitePreviewContext?: GitHubPreviewBrowserContextSnapshot;
  websitePreviewContextRef?: GitHubPreviewContextReference;
  canvasContext?: CanvasContextSnapshot;
  canvasContextRef?: CanvasContextReference;
};

type DirectSteerOptions = {
  targetModelId?: string;
  executionMode?: ResearchExecutionMode;
  reasoningEffort?: ResearchReasoningEffort;
};

type DirectMessageContext = {
  onAccepted?: () => void;
  targetModelId?: string;
  targetActiveReportId?: string | null;
  reasoningEffort?: ResearchReasoningEffort;
};

type SteerSubmissionResult = {
  accepted: boolean;
  outcome: 'accepted' | 'definitive_rejection' | 'ambiguous';
  steerId: string;
  turnId?: string;
};

type AppliedSteerReceipt = {
  steerId: string;
  turnId: string;
  sequence?: number;
};

type SteerContextSnapshot = {
  modelId: string;
  reasoningEffort: ResearchReasoningEffort | null;
  selectedSkills: SelectedAgentSkill[];
  activeReportId: string | null;
  executionMode: ResearchExecutionMode;
};

type SteerTurnResponse = {
  accepted?: boolean;
  duplicate?: boolean;
  status?: string;
  turnStatus?: 'accepting' | 'finalizing' | 'completed' | 'cancelled' | 'failed';
  steerId?: string;
  sequence?: number;
  contextSequence?: number;
  context?: SteerContextSnapshot;
  error?: {
    code?: string;
    message?: string;
  };
};

type RichScheduledCommand = ScheduledCommand & {
  reasoningEffort?: ResearchReasoningEffort;
};

type SteerReceiptState = {
  turnId: string;
  applied: boolean;
  postAccepted: boolean;
  sequence?: number;
};

type TurnCancellationResult = 'completed' | 'cancelled' | 'failed' | null;

type StreamSnapshot = {
  streamingText: string;
  thinkingText: string;
  responsePhase?: ResearchResponsePhase | null;
  streamTimeline: TraceTimelineItem[];
  activeTools: ToolStatus[];
  subagents: SubagentStatusRow[];
};

type PendingStreamUiCommit = {
  turnId: string;
  sessionId: string;
  snapshot: StreamSnapshot;
};

type RecoveredPersistedTurn = {
  messages: Message[];
  updatedAt?: string;
  contextUsage?: ContextUsage | null;
};

type PersistedTurnRecoveryOptions = {
  allowStoppedTurn?: boolean;
  timeoutMs?: number;
  pollMs?: number;
};

type PendingTurnResult =
  | {
      type: 'completed';
      optimisticUserMessageId: string;
      userMessage: Message;
      steerMessages: Message[];
      modelMessage: Message;
      baselineMessageCount: number;
      updatedAt: string;
      contextUsage?: ContextUsage | null;
      sessionName?: string | null;
      metadataApplied: boolean;
    }
  | {
      type: 'failed';
      optimisticUserMessageId: string;
      userMessage: Message;
      errorMessage: Message;
      baselineMessageCount: number;
      persistedUserMessageId?: string;
      persistedModelMessageId?: string;
      errorText: string;
      updatedAt: string;
      notified: boolean;
      metadataApplied: boolean;
    }
  | {
      type: 'recovered';
      messages: Message[];
      updatedAt?: string;
      contextUsage?: ContextUsage | null;
      metadataApplied: boolean;
    };

type ResearchChatRuntimeSubscriber = () => void;

type ResearchChatRuntimeStore = {
  ownerKey: string | null;
  foregroundSessionId: string | null;
  inFlightTurns: Map<string, InFlightTurn>;
  inFlightTurnIdBySession: Map<string, string>;
  turnSnapshots: Map<string, StreamSnapshot>;
  pendingTurnResultsBySession: Map<string, PendingTurnResult[]>;
  stoppedTurnIds: Set<string>;
  cleanedStoppedTurnIds: Set<string>;
  unreadSessionIds: Set<string>;
  messageCountsBySession: Map<string, number>;
  scheduledCommandsBySession: Map<string, ScheduledCommand[]>;
  runStatusBySession: Map<string, ResearchRunStatus>;
  dequeueInFlightSessionIds: Set<string>;
  dequeueBlockedCommandIds: Set<string>;
  cancellingSessionIds: Set<string>;
  turnCancellationPromises: Map<string, Promise<TurnCancellationResult>>;
  persistenceConfirmedTurnIds: Set<string>;
  persistedSteerIdsByTurn: Map<string, Set<string>>;
  pendingSteerIdsByTurn: Map<string, Set<string>>;
  steerReceiptStateById: Map<string, SteerReceiptState>;
  pendingSteerOptionsById: Map<string, SteerMessageOptions>;
  appliedModelIdByTurn: Map<string, string>;
  latestSteerContextSequenceByTurn: Map<string, number>;
  latestAppliedSteerReceiptBySession: Map<string, AppliedSteerReceipt>;
  ambiguousDirectSteerBySession: Map<string, Map<string, { turnId: string; steerId: string }>>;
  subscribers: Set<ResearchChatRuntimeSubscriber>;
};

const researchChatRuntimeStore: ResearchChatRuntimeStore = {
  ownerKey: null,
  foregroundSessionId: null,
  inFlightTurns: new Map<string, InFlightTurn>(),
  inFlightTurnIdBySession: new Map<string, string>(),
  turnSnapshots: new Map<string, StreamSnapshot>(),
  pendingTurnResultsBySession: new Map<string, PendingTurnResult[]>(),
  stoppedTurnIds: new Set<string>(),
  cleanedStoppedTurnIds: new Set<string>(),
  unreadSessionIds: new Set<string>(),
  messageCountsBySession: new Map<string, number>(),
  scheduledCommandsBySession: new Map<string, ScheduledCommand[]>(),
  runStatusBySession: new Map<string, ResearchRunStatus>(),
  dequeueInFlightSessionIds: new Set<string>(),
  dequeueBlockedCommandIds: new Set<string>(),
  cancellingSessionIds: new Set<string>(),
  turnCancellationPromises: new Map<string, Promise<TurnCancellationResult>>(),
  persistenceConfirmedTurnIds: new Set<string>(),
  persistedSteerIdsByTurn: new Map<string, Set<string>>(),
  pendingSteerIdsByTurn: new Map<string, Set<string>>(),
  steerReceiptStateById: new Map<string, SteerReceiptState>(),
  pendingSteerOptionsById: new Map<string, SteerMessageOptions>(),
  appliedModelIdByTurn: new Map<string, string>(),
  latestSteerContextSequenceByTurn: new Map<string, number>(),
  latestAppliedSteerReceiptBySession: new Map<string, AppliedSteerReceipt>(),
  ambiguousDirectSteerBySession: new Map(),
  subscribers: new Set<ResearchChatRuntimeSubscriber>(),
};

function rememberPersistedSteerMessages(
  turnId: string,
  messages: Message[] | undefined
): AppliedSteerReceipt[] {
  if (!messages?.length) return [];
  const persistedSteerIds =
    researchChatRuntimeStore.persistedSteerIdsByTurn.get(turnId) || new Set<string>();
  const receipts: AppliedSteerReceipt[] = [];
  for (const message of messages) {
    if (message.inputKind === 'steer' && message.steerId) {
      persistedSteerIds.add(message.steerId);
      receipts.push({
        steerId: message.steerId,
        turnId,
        ...(message.steerSequence !== undefined ? { sequence: message.steerSequence } : {}),
      });
    }
  }
  if (persistedSteerIds.size > 0) {
    researchChatRuntimeStore.persistedSteerIdsByTurn.set(turnId, persistedSteerIds);
  }
  return receipts.sort((left, right) => (left.sequence || 0) - (right.sequence || 0));
}

function isSteerTranscriptPersisted(turnId: string, steerId: string): boolean {
  return researchChatRuntimeStore.persistedSteerIdsByTurn.get(turnId)?.has(steerId) === true;
}

function getRuntimeLatestAppliedSteerReceipt(sessionId: string | null): AppliedSteerReceipt | null {
  return sessionId
    ? researchChatRuntimeStore.latestAppliedSteerReceiptBySession.get(sessionId) || null
    : null;
}

function setRuntimeLatestAppliedSteerReceipt(
  sessionId: string,
  receipt: AppliedSteerReceipt
): void {
  researchChatRuntimeStore.latestAppliedSteerReceiptBySession.set(sessionId, receipt);
  emitResearchChatRuntimeChange();
}

function emitResearchChatRuntimeChange(): void {
  for (const subscriber of researchChatRuntimeStore.subscribers) {
    subscriber();
  }
}

function resetResearchChatRuntimeStore(resetReason: string, nextOwnerKey: string | null): void {
  for (const [turnId, turn] of researchChatRuntimeStore.inFlightTurns) {
    researchChatRuntimeStore.stoppedTurnIds.add(turnId);
    turn.requestController.abort();
    if (turn.reader) {
      void turn.reader.cancel(resetReason).catch(() => {
        // Reader may already be closed by the stream handler.
      });
    }
  }

  researchChatRuntimeStore.ownerKey = nextOwnerKey;
  researchChatRuntimeStore.foregroundSessionId = null;
  researchChatRuntimeStore.inFlightTurns.clear();
  researchChatRuntimeStore.inFlightTurnIdBySession.clear();
  researchChatRuntimeStore.turnSnapshots.clear();
  researchChatRuntimeStore.pendingTurnResultsBySession.clear();
  researchChatRuntimeStore.cleanedStoppedTurnIds.clear();
  researchChatRuntimeStore.unreadSessionIds.clear();
  researchChatRuntimeStore.messageCountsBySession.clear();
  for (const commands of researchChatRuntimeStore.scheduledCommandsBySession.values()) {
    for (const command of commands) {
      command.documents?.forEach(document => {
        void deleteUploadedDocument(document).catch(() => undefined);
      });
    }
  }
  researchChatRuntimeStore.scheduledCommandsBySession.clear();
  researchChatRuntimeStore.runStatusBySession.clear();
  researchChatRuntimeStore.dequeueInFlightSessionIds.clear();
  researchChatRuntimeStore.dequeueBlockedCommandIds.clear();
  researchChatRuntimeStore.cancellingSessionIds.clear();
  researchChatRuntimeStore.turnCancellationPromises.clear();
  researchChatRuntimeStore.persistenceConfirmedTurnIds.clear();
  researchChatRuntimeStore.persistedSteerIdsByTurn.clear();
  researchChatRuntimeStore.pendingSteerIdsByTurn.clear();
  researchChatRuntimeStore.steerReceiptStateById.clear();
  researchChatRuntimeStore.pendingSteerOptionsById.clear();
  researchChatRuntimeStore.appliedModelIdByTurn.clear();
  researchChatRuntimeStore.latestSteerContextSequenceByTurn.clear();
  researchChatRuntimeStore.latestAppliedSteerReceiptBySession.clear();
  researchChatRuntimeStore.ambiguousDirectSteerBySession.clear();
  emitResearchChatRuntimeChange();
}

export function syncResearchChatRuntimeOwner(nextOwnerKey: string | null): boolean {
  const normalizedOwnerKey = nextOwnerKey || null;
  if (researchChatRuntimeStore.ownerKey === normalizedOwnerKey) return false;
  resetResearchChatRuntimeStore('owner_change', normalizedOwnerKey);
  return true;
}

function getRuntimeActiveSessionIds(): string[] {
  return Array.from(
    new Set(Array.from(researchChatRuntimeStore.inFlightTurns.values()).map(turn => turn.sessionId))
  ).sort();
}

function getRuntimeUnreadSessionIds(): string[] {
  return Array.from(researchChatRuntimeStore.unreadSessionIds).sort();
}

function getRuntimeInFlightTurnId(sessionId: string | null): string | null {
  if (!sessionId) return null;
  return researchChatRuntimeStore.inFlightTurnIdBySession.get(sessionId) || null;
}

function getRuntimeInFlightTurn(sessionId: string | null): InFlightTurn | null {
  const turnId = getRuntimeInFlightTurnId(sessionId);
  return turnId ? researchChatRuntimeStore.inFlightTurns.get(turnId) || null : null;
}

function isRuntimeSessionLoading(sessionId: string | null): boolean {
  if (sessionId && researchChatRuntimeStore.cancellingSessionIds.has(sessionId)) return true;
  const turnId = getRuntimeInFlightTurnId(sessionId);
  return Boolean(turnId && !researchChatRuntimeStore.stoppedTurnIds.has(turnId));
}

function getRuntimeMessageCount(sessionId: string | null): number {
  if (!sessionId) return 0;
  return researchChatRuntimeStore.messageCountsBySession.get(sessionId) || 0;
}

function setRuntimeMessageCount(sessionId: string | null, messageCount: number): void {
  if (!sessionId) return;
  researchChatRuntimeStore.messageCountsBySession.set(sessionId, Math.max(0, messageCount));
}

function syncRuntimeMessageCountFromLiveSession(
  sessionId: string | null,
  messageCount: number
): void {
  if (!sessionId) return;
  const pendingResults = getRuntimePendingTurnResults(sessionId);
  if (pendingResults.some(pendingResult => !pendingResult.metadataApplied)) return;
  setRuntimeMessageCount(sessionId, messageCount);
}

function getRuntimePendingTurnResults(sessionId: string | null): PendingTurnResult[] {
  if (!sessionId) return [];
  return researchChatRuntimeStore.pendingTurnResultsBySession.get(sessionId) || [];
}

function setRuntimePendingTurnResults(
  sessionId: string | null,
  results: PendingTurnResult[]
): void {
  if (!sessionId) return;
  if (results.length === 0) {
    researchChatRuntimeStore.pendingTurnResultsBySession.delete(sessionId);
    return;
  }
  researchChatRuntimeStore.pendingTurnResultsBySession.set(sessionId, results);
}

function appendRuntimePendingTurnResult(sessionId: string | null, result: PendingTurnResult): void {
  if (!sessionId) return;
  setRuntimePendingTurnResults(sessionId, [...getRuntimePendingTurnResults(sessionId), result]);
}

function pruneAppliedRuntimePendingTurnResults(sessionId: string | null): void {
  if (!sessionId) return;
  const pendingResults = getRuntimePendingTurnResults(sessionId);
  if (pendingResults.length === 0) return;
  setRuntimePendingTurnResults(
    sessionId,
    pendingResults.filter(pendingResult => !pendingResult.metadataApplied)
  );
}

function getFailedTurnMessageCount(
  baselineMessageCount: number,
  persistedUserMessageId: string | undefined,
  persistedModelMessageId: string | undefined,
  isLocalSession: boolean
): number {
  if (isLocalSession) return baselineMessageCount + 2;
  if (!persistedUserMessageId) return baselineMessageCount;
  return baselineMessageCount + (persistedModelMessageId ? 2 : 1);
}

function getEmptyTurnMessageCount(
  baselineMessageCount: number,
  persistedUserMessageId: string | undefined,
  isLocalSession: boolean
): number {
  if (isLocalSession) return baselineMessageCount;
  return persistedUserMessageId ? baselineMessageCount + 1 : baselineMessageCount;
}

function getRuntimeScheduledCommands(sessionId: string | null): ScheduledCommand[] {
  if (!sessionId) return [];
  return researchChatRuntimeStore.scheduledCommandsBySession.get(sessionId) || [];
}

function setRuntimeScheduledCommands(
  sessionId: string | null,
  updater: ScheduledCommand[] | ((current: ScheduledCommand[]) => ScheduledCommand[])
): ScheduledCommand[] {
  if (!sessionId) return [];
  const current = getRuntimeScheduledCommands(sessionId);
  const next = typeof updater === 'function' ? updater(current) : updater;
  if (next.length === 0) {
    researchChatRuntimeStore.scheduledCommandsBySession.delete(sessionId);
  } else {
    researchChatRuntimeStore.scheduledCommandsBySession.set(sessionId, next);
  }
  emitResearchChatRuntimeChange();
  return next;
}

function getSteerToastId(steerId: string): string {
  return `research-steer-${steerId}`;
}

function getScheduledCommandSteerId(command: ScheduledCommand): string {
  return `queued-${command.id}`;
}

function getRuntimeRunStatus(sessionId: string | null): ResearchRunStatus | null {
  if (!sessionId) return null;
  return researchChatRuntimeStore.runStatusBySession.get(sessionId) || null;
}

function setRuntimeRunStatus(
  sessionId: string | null,
  next: ResearchRunStatus | null
): ResearchRunStatus | null {
  if (!sessionId) return null;
  const current = getRuntimeRunStatus(sessionId);
  if (!next) {
    researchChatRuntimeStore.runStatusBySession.delete(sessionId);
    emitResearchChatRuntimeChange();
    return null;
  }
  if (current?.runId === next.runId && current.sequence >= next.sequence) return current;
  researchChatRuntimeStore.runStatusBySession.set(sessionId, next);
  emitResearchChatRuntimeChange();
  return next;
}

function aggregateSourcesFromToolUsage(toolUsage?: ToolStatus[]): SourceReference[] | undefined {
  if (!toolUsage || toolUsage.length === 0) return undefined;

  const allSources: SourceReference[] = [];
  for (const tool of toolUsage) {
    if (tool.sources && tool.sources.length > 0) {
      allSources.push(...tool.sources);
    }
  }

  return allSources.length > 0 ? allSources : undefined;
}

function hydrateMessageSources(messages: Message[]): Message[] {
  return messages.map(message => {
    const toolUsage = message.toolUsage?.map(finalizePendingToolStatus);
    const traceTimeline = message.traceTimeline?.map(item =>
      item.type === 'tool' ? finalizePendingToolStatus(item) : item
    );
    return {
      ...message,
      toolUsage,
      traceTimeline,
      sources: message.sources || aggregateSourcesFromToolUsage(toolUsage),
    };
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function waitForRuntimeSteeringWindow(sessionId: string, turnId: string): Promise<boolean> {
  while (getRuntimeInFlightTurnId(sessionId) === turnId) {
    const turn = researchChatRuntimeStore.inFlightTurns.get(turnId);
    if (!turn || researchChatRuntimeStore.stoppedTurnIds.has(turnId)) return false;
    if (turn.steeringReady) return true;
    await sleep(50);
  }
  return false;
}

function getImageSignature(images?: ImageAttachment[]): string {
  if (!images || images.length === 0) return '';

  return images
    .map(image =>
      [
        image.id,
        image.mimeType,
        image.name || '',
        image.width || '',
        image.height || '',
        image.previewAnnotation ? JSON.stringify(image.previewAnnotation) : '',
      ].join(':')
    )
    .join('|');
}

function getDocumentSignature(documents?: DocumentAttachment[]): string {
  return (documents || [])
    .map(document =>
      [document.id, document.name, document.mimeType, document.sizeBytes, document.sha256].join(':')
    )
    .join('|');
}

function getSelectedSkillSignature(skills?: SelectedAgentSkill[]): string {
  return (skills || [])
    .map(skill => `${skill.skillId}:${skill.versionId || ''}`)
    .sort()
    .join('|');
}

function getDirectSteerSignature(
  turnId: string,
  options: Omit<SteerMessageOptions, 'steerId' | 'targetSessionId'>
): string {
  return JSON.stringify({
    turnId,
    content: options.content.trim(),
    images: (options.images || []).map(image => [
      image.id,
      image.mimeType,
      image.base64Data.length,
      image.previewAnnotation,
    ]),
    documents: (options.documents || []).map(document => [
      document.id,
      document.sha256,
      document.sizeBytes,
    ]),
    selectedSkills:
      options.selectedSkills === undefined
        ? undefined
        : [...options.selectedSkills]
            .map(skill => [skill.skillId, skill.versionId || ''])
            .sort(([left], [right]) => left.localeCompare(right)),
    targetModelId: options.targetModelId,
    reasoningEffort: options.reasoningEffort,
    activeReportId: options.targetActiveReportId,
    executionMode: options.executionMode,
    websitePreviewContextRef: options.websitePreviewContextRef,
    canvasContextRef: options.canvasContextRef,
  });
}

function getSteerMessageContent(options: SteerMessageOptions): string {
  const content = options.content.trim();
  if (content) return content;
  if (options.documents?.length) return '(See attached document)';
  if (options.images?.length) return '(See attached image)';
  return '(Turn configuration updated)';
}

function parseSteerContextSnapshot(value: unknown): SteerContextSnapshot | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (
    typeof record.modelId !== 'string' ||
    !record.modelId ||
    !(
      record.executionMode === 'standard' ||
      record.executionMode === 'plan' ||
      record.executionMode === 'goal'
    ) ||
    !(record.activeReportId === null || typeof record.activeReportId === 'string') ||
    !Array.isArray(record.selectedSkills)
  ) {
    return null;
  }
  const reasoningEffort =
    record.reasoningEffort === null ? null : resolveResearchReasoningEffort(record.reasoningEffort);
  if (reasoningEffort === undefined) return null;
  const selectedSkills = record.selectedSkills.flatMap(candidate => {
    if (
      !candidate ||
      typeof candidate !== 'object' ||
      Array.isArray(candidate) ||
      typeof (candidate as Record<string, unknown>).skillId !== 'string'
    ) {
      return [];
    }
    const selected = candidate as Record<string, unknown>;
    return [
      {
        skillId: selected.skillId as string,
        ...(typeof selected.versionId === 'string' ? { versionId: selected.versionId } : {}),
      },
    ];
  });
  if (selectedSkills.length !== record.selectedSkills.length) return null;
  return {
    modelId: record.modelId,
    reasoningEffort,
    selectedSkills,
    activeReportId: record.activeReportId as string | null,
    executionMode: record.executionMode,
  };
}

function isSameUserTurn(candidate: Message, userMessage: Message): boolean {
  if (candidate.turnId && userMessage.turnId) return candidate.turnId === userMessage.turnId;
  return (
    candidate.role === 'user' &&
    candidate.content === userMessage.content &&
    (candidate.commandId || null) === (userMessage.commandId || null) &&
    getImageSignature(candidate.images) === getImageSignature(userMessage.images) &&
    getDocumentSignature(candidate.documents) === getDocumentSignature(userMessage.documents) &&
    getSelectedSkillSignature(candidate.selectedSkills) ===
      getSelectedSkillSignature(userMessage.selectedSkills) &&
    (candidate.executionMode || 'standard') === (userMessage.executionMode || 'standard')
  );
}

function isSameModelTurn(candidate: Message, modelMessage: Message): boolean {
  if (candidate.runReceipt?.runId && modelMessage.runReceipt?.runId) {
    return (
      candidate.runReceipt.runId === modelMessage.runReceipt.runId &&
      candidate.runReceipt.sequence === modelMessage.runReceipt.sequence
    );
  }
  return (
    candidate.role === 'model' &&
    candidate.content === modelMessage.content &&
    (candidate.commandId || null) === (modelMessage.commandId || null)
  );
}

function hasPersistedModelTurn(
  messages: Message[],
  userMessage: Message,
  baselineMessageCount: number,
  persistedUserMessageId?: string,
  persistedModelMessageId?: string
): boolean {
  if (persistedModelMessageId) {
    return messages.some(
      message =>
        message.role === 'model' &&
        message.id === persistedModelMessageId &&
        !message.id.startsWith('error-') &&
        (message.content.trim().length > 0 ||
          Boolean(message.runStatus) ||
          Boolean(message.runReceipt))
    );
  }

  const userSearchStartIndex = Math.min(baselineMessageCount, messages.length);
  const userMessageIndex = messages.findIndex(
    (message, index) =>
      index >= userSearchStartIndex &&
      message.role === 'user' &&
      ((persistedUserMessageId && message.id === persistedUserMessageId) ||
        isSameUserTurn(message, userMessage))
  );
  if (userMessageIndex < 0) return false;

  for (let index = userMessageIndex + 1; index < messages.length; index += 1) {
    const message = messages[index];
    if (message.role === 'user') return false;
    if (
      message.role === 'model' &&
      !message.id.startsWith('error-') &&
      (message.content.trim().length > 0 ||
        Boolean(message.runStatus) ||
        Boolean(message.runReceipt)) &&
      (message.commandId || null) === (userMessage.commandId || null)
    ) {
      return true;
    }
  }

  return false;
}

function arraysEqual(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export function useResearchChat({
  sessionId,
  runtimeOwnerKey,
  messages,
  apiCall,
  selectedModelId,
  reasoningEffortModelIds,
  setMessages,
  setSessionUpdatedAt,
  setContextUsage,
  setSessionName,
  getActiveReportId,
  getWebsitePreviewContextSnapshot,
  getWebsitePreviewContextReference,
  clearWebsitePreviewSelection,
  getCanvasContextSnapshot,
  getCanvasContextReference,
  clearCanvasSelection,
  canvasEditingActive = false,
  designSystemEditingActive = false,
}: UseResearchChatProps): UseResearchChatReturn {
  const normalizedRuntimeOwnerKey = runtimeOwnerKey || null;
  const runtimeOwnerMatches = researchChatRuntimeStore.ownerKey === normalizedRuntimeOwnerKey;
  const [isLoading, setIsLoading] = useState(() =>
    runtimeOwnerMatches ? isRuntimeSessionLoading(sessionId) : false
  );
  const [voiceRefresh, setVoiceRefresh] = useState(0);
  useEffect(() => {
    const onVoiceResult = (event: Event) => {
      if ((event as CustomEvent<{ sessionId: string }>).detail?.sessionId === sessionId)
        setVoiceRefresh(v => v + 1);
    };
    window.addEventListener(VOICE_CONVERSATION_CHANGED_EVENT, onVoiceResult);
    return () => window.removeEventListener(VOICE_CONVERSATION_CHANGED_EVENT, onVoiceResult);
  }, [sessionId]);
  useEffect(() => {
    if (!voiceRefresh || !sessionId || isLoading) return;
    let active = true;
    void apiCall({ action: 'get_session', sessionId })
      .then(async response => {
        if (!response.ok) return;
        const data = (await response.json()) as SessionRecoveryResponse;
        if (active && data.session?.messages) setMessages(data.session.messages);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [voiceRefresh, sessionId, isLoading, apiCall, setMessages]);
  const [streamingText, setStreamingText] = useState('');
  const [thinkingText, setThinkingText] = useState('');
  const [responsePhase, setResponsePhase] = useState<ResearchResponsePhase | null>(null);
  const [streamTimeline, setStreamTimeline] = useState<TraceTimelineItem[]>([]);
  const [activeTools, setActiveTools] = useState<ToolStatus[]>([]);
  const [subagents, setSubagents] = useState<SubagentStatusRow[]>([]);
  const pendingSubagentControlsRef = useRef(new Set<string>());
  const [runStatus, setRunStatus] = useState<ResearchRunStatus | null>(() =>
    runtimeOwnerMatches ? getRuntimeRunStatus(sessionId) : null
  );
  const [activeSessionIds, setActiveSessionIds] = useState<string[]>(() =>
    runtimeOwnerMatches ? getRuntimeActiveSessionIds() : []
  );
  const [unreadSessionIds, setUnreadSessionIds] = useState<string[]>(() =>
    runtimeOwnerMatches ? getRuntimeUnreadSessionIds() : []
  );
  const [scheduledCommands, setScheduledCommands] = useState<ScheduledCommand[]>(() =>
    runtimeOwnerMatches ? getRuntimeScheduledCommands(sessionId) : []
  );
  const [latestAppliedSteerReceipt, setLatestAppliedSteerReceipt] =
    useState<AppliedSteerReceipt | null>(() =>
      runtimeOwnerMatches ? getRuntimeLatestAppliedSteerReceipt(sessionId) : null
    );
  const [inputMessage, setInputMessage] = useState('');
  const [runtimeRevision, setRuntimeRevision] = useState(0);
  const inFlightTurnsRef = useRef(researchChatRuntimeStore.inFlightTurns);
  const inFlightTurnIdBySessionRef = useRef(researchChatRuntimeStore.inFlightTurnIdBySession);
  const turnSnapshotsRef = useRef(researchChatRuntimeStore.turnSnapshots);
  const stoppedTurnIdsRef = useRef(researchChatRuntimeStore.stoppedTurnIds);
  const pendingSteerIdsByTurnRef = useRef(researchChatRuntimeStore.pendingSteerIdsByTurn);
  const steerReceiptStateByIdRef = useRef(researchChatRuntimeStore.steerReceiptStateById);
  const pendingSteerOptionsByIdRef = useRef(researchChatRuntimeStore.pendingSteerOptionsById);
  const appliedModelIdByTurnRef = useRef(researchChatRuntimeStore.appliedModelIdByTurn);
  const latestSteerContextSequenceByTurnRef = useRef(
    researchChatRuntimeStore.latestSteerContextSequenceByTurn
  );
  const drainQueuedSessionRef = useRef<(queuedSessionId: string) => Promise<void>>(async () => {});
  const foregroundTurnIdRef = useRef<string | null>(
    runtimeOwnerMatches ? getRuntimeInFlightTurnId(sessionId) : null
  );
  const mountedRef = useRef(true);
  const sendMessageRef = useRef<() => Promise<void>>(async () => {});
  const activeSessionIdRef = useRef<string | null>(sessionId);
  const recoveryGenerationRef = useRef(0);
  const pendingStreamUiCommitRef = useRef<PendingStreamUiCommit | null>(null);
  const streamUiFrameRef = useRef<number | null>(null);
  const streamUiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStreamUiFlushAtRef = useRef(0);

  const isForegroundTurn = useCallback(
    (turnId: string, turnSessionId: string) =>
      mountedRef.current &&
      foregroundTurnIdRef.current === turnId &&
      activeSessionIdRef.current === turnSessionId,
    []
  );

  const reconcilePersistedSteerMessages = useCallback(
    (turnId: string, turnSessionId: string, persistedMessages: Message[] | undefined) => {
      const receipts = rememberPersistedSteerMessages(turnId, persistedMessages);
      if (receipts.length === 0) return;
      for (const receipt of receipts) {
        pendingSteerIdsByTurnRef.current.get(turnId)?.delete(receipt.steerId);
        const prior = steerReceiptStateByIdRef.current.get(receipt.steerId);
        steerReceiptStateByIdRef.current.set(receipt.steerId, {
          turnId,
          applied: true,
          postAccepted: prior?.postAccepted || false,
          ...(receipt.sequence !== undefined ? { sequence: receipt.sequence } : {}),
        });
      }
      const ambiguousSteerIds = new Set(
        [
          ...(researchChatRuntimeStore.ambiguousDirectSteerBySession.get(turnSessionId)?.values() ||
            []),
        ]
          .filter(candidate => candidate.turnId === turnId)
          .map(candidate => candidate.steerId)
      );
      const newestFirst = [...receipts].reverse();
      setRuntimeLatestAppliedSteerReceipt(
        turnSessionId,
        newestFirst.find(receipt => ambiguousSteerIds.has(receipt.steerId)) || newestFirst[0]
      );
    },
    []
  );

  const hydratePersistedSteerMessages = useCallback((persistedMessages: Message[] | undefined) => {
    if (!persistedMessages?.length) return [];
    return persistedMessages.map(message => {
      if (message.inputKind !== 'steer' || !message.steerId) return message;
      const pending = pendingSteerOptionsByIdRef.current.get(message.steerId);
      if (!pending) return message;
      return {
        ...message,
        images: message.images || pending.images,
        documents: message.documents || pending.documents,
        selectedSkills:
          message.selectedSkills === undefined ? pending.selectedSkills : message.selectedSkills,
        targetModelId: message.targetModelId || pending.targetModelId,
        reasoningEffort:
          message.reasoningEffort === undefined ? pending.reasoningEffort : message.reasoningEffort,
      };
    });
  }, []);

  const commitStreamUiSnapshot = useCallback((snapshot: StreamSnapshot) => {
    setStreamingText(snapshot.streamingText);
    setThinkingText(snapshot.thinkingText);
    setResponsePhase(snapshot.responsePhase || null);
    setStreamTimeline(snapshot.streamTimeline);
    setActiveTools(snapshot.activeTools);
    setSubagents(snapshot.subagents);
  }, []);

  const cancelPendingStreamUiCommit = useCallback(() => {
    if (streamUiTimerRef.current !== null) {
      clearTimeout(streamUiTimerRef.current);
      streamUiTimerRef.current = null;
    }
    if (streamUiFrameRef.current !== null) {
      window.cancelAnimationFrame(streamUiFrameRef.current);
      streamUiFrameRef.current = null;
    }
    pendingStreamUiCommitRef.current = null;
    lastStreamUiFlushAtRef.current = 0;
  }, []);

  const flushPendingStreamUiCommit = useCallback(() => {
    streamUiFrameRef.current = null;
    const pendingCommit = pendingStreamUiCommitRef.current;
    pendingStreamUiCommitRef.current = null;
    if (!pendingCommit || !isForegroundTurn(pendingCommit.turnId, pendingCommit.sessionId)) {
      return;
    }
    lastStreamUiFlushAtRef.current = Date.now();
    commitStreamUiSnapshot(pendingCommit.snapshot);
  }, [commitStreamUiSnapshot, isForegroundTurn]);

  const scheduleStreamUiCommit = useCallback(
    (pendingCommit: PendingStreamUiCommit) => {
      pendingStreamUiCommitRef.current = pendingCommit;
      if (streamUiTimerRef.current !== null || streamUiFrameRef.current !== null) {
        return;
      }

      const requestFlushFrame = () => {
        streamUiTimerRef.current = null;
        streamUiFrameRef.current = window.requestAnimationFrame(flushPendingStreamUiCommit);
      };
      const elapsedMs = Date.now() - lastStreamUiFlushAtRef.current;
      const delayMs = Math.max(0, STREAM_UI_FLUSH_INTERVAL_MS - elapsedMs);
      if (delayMs > 0) {
        streamUiTimerRef.current = setTimeout(requestFlushFrame, delayMs);
        return;
      }
      requestFlushFrame();
    },
    [flushPendingStreamUiCommit]
  );

  const clearLiveStreamUi = useCallback(() => {
    cancelPendingStreamUiCommit();
    commitStreamUiSnapshot({
      streamingText: '',
      thinkingText: '',
      streamTimeline: [],
      activeTools: [],
      subagents: [],
    });
  }, [cancelPendingStreamUiCommit, commitStreamUiSnapshot]);

  useEffect(() => {
    if (syncResearchChatRuntimeOwner(normalizedRuntimeOwnerKey)) {
      foregroundTurnIdRef.current = null;
      pendingSteerIdsByTurnRef.current.clear();
      steerReceiptStateByIdRef.current.clear();
      pendingSteerOptionsByIdRef.current.clear();
      appliedModelIdByTurnRef.current.clear();
      latestSteerContextSequenceByTurnRef.current.clear();
      setLatestAppliedSteerReceipt(null);
      recoveryGenerationRef.current += 1;
      setIsLoading(false);
      setActiveSessionIds([]);
      setUnreadSessionIds([]);
      setScheduledCommands([]);
      setRunStatus(null);
      clearLiveStreamUi();
      setRuntimeRevision(current => current + 1);
    }
  }, [clearLiveStreamUi, normalizedRuntimeOwnerKey]);

  const applyTurnSnapshot = useCallback(
    (turnId: string, turnSessionId: string, patch: Partial<StreamSnapshot>) => {
      const previous = turnSnapshotsRef.current.get(turnId) || {
        streamingText: '',
        thinkingText: '',
        streamTimeline: [],
        activeTools: [],
        subagents: [],
      };
      const next = { ...previous, ...patch };
      turnSnapshotsRef.current.set(turnId, next);

      if (!isForegroundTurn(turnId, turnSessionId)) return;
      scheduleStreamUiCommit({
        turnId,
        sessionId: turnSessionId,
        snapshot: next,
      });
    },
    [isForegroundTurn, scheduleStreamUiCommit]
  );

  const restoreForegroundTurnSnapshot = useCallback(
    (turnId: string | null) => {
      cancelPendingStreamUiCommit();
      if (!turnId) {
        commitStreamUiSnapshot({
          streamingText: '',
          thinkingText: '',
          streamTimeline: [],
          activeTools: [],
          subagents: [],
        });
        return;
      }
      const snapshot = turnSnapshotsRef.current.get(turnId);
      if (!snapshot) {
        commitStreamUiSnapshot({
          streamingText: '',
          thinkingText: '',
          streamTimeline: [],
          activeTools: [],
          subagents: [],
        });
        return;
      }
      commitStreamUiSnapshot(snapshot);
    },
    [cancelPendingStreamUiCommit, commitStreamUiSnapshot]
  );

  const clearTurnLiveState = useCallback(
    (turnId: string, turnSessionId: string) => {
      const emptySnapshot: StreamSnapshot = {
        streamingText: '',
        thinkingText: '',
        streamTimeline: [],
        activeTools: [],
        subagents: [],
      };
      turnSnapshotsRef.current.set(turnId, emptySnapshot);
      if (!isForegroundTurn(turnId, turnSessionId)) return;
      cancelPendingStreamUiCommit();
      commitStreamUiSnapshot(emptySnapshot);
    },
    [cancelPendingStreamUiCommit, commitStreamUiSnapshot, isForegroundTurn]
  );

  const cleanupStoppedTurnUi = useCallback(
    (turnId: string, turnSessionId: string, optimisticMessageId: string): boolean => {
      if (
        researchChatRuntimeStore.cleanedStoppedTurnIds.has(turnId) ||
        !mountedRef.current ||
        activeSessionIdRef.current !== turnSessionId
      ) {
        return false;
      }

      researchChatRuntimeStore.cleanedStoppedTurnIds.add(turnId);
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessageId));
      clearTurnLiveState(turnId, turnSessionId);
      setIsLoading(isRuntimeSessionLoading(turnSessionId));
      toast.success('Response stopped');
      emitResearchChatRuntimeChange();
      return true;
    },
    [clearTurnLiveState, setMessages]
  );

  const syncActiveSessionIds = useCallback(() => {
    const nextSessionIds = getRuntimeActiveSessionIds();
    if (mountedRef.current) {
      setActiveSessionIds(prev => (arraysEqual(prev, nextSessionIds) ? prev : nextSessionIds));
    }
    emitResearchChatRuntimeChange();
  }, []);

  const markSessionUnread = useCallback((unreadSessionId: string) => {
    if (researchChatRuntimeStore.foregroundSessionId === unreadSessionId) {
      researchChatRuntimeStore.unreadSessionIds.delete(unreadSessionId);
      if (mountedRef.current) {
        setUnreadSessionIds(getRuntimeUnreadSessionIds());
      }
      emitResearchChatRuntimeChange();
      return;
    }
    researchChatRuntimeStore.unreadSessionIds.add(unreadSessionId);
    if (mountedRef.current) {
      setUnreadSessionIds(getRuntimeUnreadSessionIds());
    }
    emitResearchChatRuntimeChange();
  }, []);

  const ensureTurnMessages = useCallback(
    (
      turnSessionId: string,
      optimisticUserMessageId: string,
      userMessage: Message,
      followupMessage?: Message | Message[],
      baselineMessageCount = 0
    ) => {
      if (activeSessionIdRef.current !== turnSessionId) return;

      setMessages(prev => {
        const userSearchStartIndex = Math.min(baselineMessageCount, prev.length);
        const currentTurnMessages = prev.slice(userSearchStartIndex);
        const existingUserMessage =
          currentTurnMessages.find(
            msg => msg.id === userMessage.id && msg.id !== optimisticUserMessageId
          ) ||
          currentTurnMessages.find(
            msg => msg.id !== optimisticUserMessageId && isSameUserTurn(msg, userMessage)
          ) ||
          currentTurnMessages.find(msg => msg.id === optimisticUserMessageId);
        const hasOptimisticUserMessage = prev.some(msg => msg.id === optimisticUserMessageId);
        const userMessageAlreadyPresent = hasOptimisticUserMessage || Boolean(existingUserMessage);
        let userMessagesChanged = false;
        const withPersistedUserId = prev.flatMap(msg => {
          if (msg.id !== optimisticUserMessageId || msg.id === userMessage.id) {
            return [msg];
          }
          if (existingUserMessage && existingUserMessage.id !== optimisticUserMessageId) {
            userMessagesChanged = true;
            return [];
          }
          userMessagesChanged = true;
          return [
            {
              ...userMessage,
              images: msg.images || userMessage.images,
              documents: msg.documents || userMessage.documents,
            },
          ];
        });
        const messagesWithUser = userMessageAlreadyPresent
          ? withPersistedUserId
          : [...withPersistedUserId, userMessage];
        if (!userMessageAlreadyPresent) {
          userMessagesChanged = true;
        }

        if (!followupMessage) {
          return userMessagesChanged ? messagesWithUser : prev;
        }

        const followupMessages = Array.isArray(followupMessage)
          ? followupMessage
          : [followupMessage];
        let messagesWithFollowups = messagesWithUser;
        for (const followup of followupMessages) {
          const searchStartIndex = Math.min(baselineMessageCount, messagesWithFollowups.length);
          const matchingSteerIndex =
            followup.inputKind === 'steer' && followup.steerId
              ? messagesWithFollowups
                  .slice(searchStartIndex)
                  .findIndex(
                    message => message.inputKind === 'steer' && message.steerId === followup.steerId
                  )
              : -1;
          if (matchingSteerIndex >= 0) {
            const absoluteSteerIndex = searchStartIndex + matchingSteerIndex;
            const optimisticSteer = messagesWithFollowups[absoluteSteerIndex];
            messagesWithFollowups = messagesWithFollowups.map((message, index) =>
              index === absoluteSteerIndex
                ? {
                    ...optimisticSteer,
                    ...followup,
                    images: followup.images || optimisticSteer.images,
                    documents: followup.documents || optimisticSteer.documents,
                    selectedSkills:
                      followup.selectedSkills === undefined
                        ? optimisticSteer.selectedSkills
                        : followup.selectedSkills,
                  }
                : message
            );
            continue;
          }
          const alreadyPresent = messagesWithFollowups
            .slice(searchStartIndex)
            .some(
              message =>
                message.id === followup.id ||
                (followup.role === 'model' && isSameModelTurn(message, followup))
            );
          if (!alreadyPresent) {
            messagesWithFollowups = [...messagesWithFollowups, followup];
          }
        }
        if (messagesWithFollowups !== messagesWithUser) return messagesWithFollowups;
        return userMessagesChanged ? messagesWithUser : prev;
      });
    },
    [setMessages]
  );

  const beginTurn = useCallback(
    (
      turnId: string,
      turnSessionId: string,
      requestController: AbortController,
      optimisticMessage: Message,
      baselineMessageCount: number,
      makeForeground: boolean,
      activeReportSelectionBaseline: string | null
    ) => {
      pruneAppliedRuntimePendingTurnResults(turnSessionId);
      if (!researchChatRuntimeStore.dequeueInFlightSessionIds.has(turnSessionId)) {
        for (const command of getRuntimeScheduledCommands(turnSessionId)) {
          researchChatRuntimeStore.dequeueBlockedCommandIds.delete(command.id);
        }
      }
      inFlightTurnsRef.current.set(turnId, {
        sessionId: turnSessionId,
        requestController,
        reader: null,
        steeringReady: false,
        optimisticMessage,
        baselineMessageCount,
        activeReportSelectionBaseline,
      });
      syncActiveSessionIds();
      inFlightTurnIdBySessionRef.current.set(turnSessionId, turnId);
      turnSnapshotsRef.current.set(turnId, {
        streamingText: '',
        thinkingText: '',
        streamTimeline: [],
        activeTools: [],
        subagents: [],
      });
      if (makeForeground) {
        foregroundTurnIdRef.current = turnId;
      }
      return turnId;
    },
    [syncActiveSessionIds]
  );

  const finishTurn = useCallback(
    (turnId: string, turnSessionId: string) => {
      const mappedTurnId = inFlightTurnIdBySessionRef.current.get(turnSessionId);
      if (mappedTurnId === turnId) {
        inFlightTurnIdBySessionRef.current.delete(turnSessionId);
      }
      const ambiguousSteers =
        researchChatRuntimeStore.ambiguousDirectSteerBySession.get(turnSessionId);
      if (ambiguousSteers) {
        for (const [signature, ambiguousSteer] of ambiguousSteers) {
          if (ambiguousSteer.turnId === turnId) ambiguousSteers.delete(signature);
        }
        if (ambiguousSteers.size === 0) {
          researchChatRuntimeStore.ambiguousDirectSteerBySession.delete(turnSessionId);
        }
      }
      inFlightTurnsRef.current.delete(turnId);
      stoppedTurnIdsRef.current.delete(turnId);
      researchChatRuntimeStore.cleanedStoppedTurnIds.delete(turnId);
      turnSnapshotsRef.current.delete(turnId);
      if (foregroundTurnIdRef.current === turnId) {
        foregroundTurnIdRef.current = null;
      }
      syncActiveSessionIds();
    },
    [syncActiveSessionIds]
  );

  const claimSteerContextSequence = useCallback((turnId: string, sequence?: number) => {
    const activeTurn = inFlightTurnsRef.current.get(turnId);
    if (!activeTurn) return false;
    const latest = latestSteerContextSequenceByTurnRef.current.get(turnId) || 0;
    if (sequence === undefined) return latest === 0;
    if (!Number.isSafeInteger(sequence) || sequence < latest) return false;
    latestSteerContextSequenceByTurnRef.current.set(turnId, sequence);
    return true;
  }, []);

  const applyAcceptedSteerContext = useCallback(
    (turnId: string, options: SteerMessageOptions, sequence?: number) => {
      if (!claimSteerContextSequence(turnId, sequence)) return;
      const activeTurn = inFlightTurnsRef.current.get(turnId)!;
      const hasReasoningEffort = Object.prototype.hasOwnProperty.call(options, 'reasoningEffort');
      const nextReasoningEffort = hasReasoningEffort
        ? options.reasoningEffort
        : options.targetModelId && !reasoningEffortModelIds?.includes(options.targetModelId)
          ? null
          : activeTurn.optimisticMessage.reasoningEffort;
      activeTurn.optimisticMessage = {
        ...activeTurn.optimisticMessage,
        ...(options.selectedSkills !== undefined ? { selectedSkills: options.selectedSkills } : {}),
        ...(options.targetModelId !== undefined ? { targetModelId: options.targetModelId } : {}),
        ...(hasReasoningEffort || options.targetModelId !== undefined
          ? { reasoningEffort: nextReasoningEffort }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(options, 'targetActiveReportId')
          ? { activeReportId: options.targetActiveReportId }
          : {}),
        ...(options.executionMode !== undefined ? { executionMode: options.executionMode } : {}),
      };
      if (Object.prototype.hasOwnProperty.call(options, 'targetActiveReportId')) {
        activeTurn.activeReportSelectionBaseline = options.targetActiveReportId ?? null;
      }
      emitResearchChatRuntimeChange();
    },
    [claimSteerContextSequence, reasoningEffortModelIds]
  );

  const applyResolvedSteerContext = useCallback(
    (turnId: string, context: SteerContextSnapshot, sequence: number) => {
      if (!claimSteerContextSequence(turnId, sequence)) return;
      const activeTurn = inFlightTurnsRef.current.get(turnId)!;
      activeTurn.optimisticMessage = {
        ...activeTurn.optimisticMessage,
        targetModelId: context.modelId,
        reasoningEffort: context.reasoningEffort,
        selectedSkills: context.selectedSkills,
        activeReportId: context.activeReportId,
        executionMode: context.executionMode,
      };
      activeTurn.activeReportSelectionBaseline = context.activeReportId;
      emitResearchChatRuntimeChange();
    },
    [claimSteerContextSequence]
  );

  useEffect(() => {
    activeSessionIdRef.current = sessionId;
    recoveryGenerationRef.current += 1;
    researchChatRuntimeStore.foregroundSessionId = sessionId;
    if (sessionId) {
      researchChatRuntimeStore.unreadSessionIds.delete(sessionId);
    }
    setUnreadSessionIds(getRuntimeUnreadSessionIds());
    setScheduledCommands(getRuntimeScheduledCommands(sessionId));
    setRunStatus(getRuntimeRunStatus(sessionId));
    setLatestAppliedSteerReceipt(getRuntimeLatestAppliedSteerReceipt(sessionId));
    setActiveSessionIds(getRuntimeActiveSessionIds());
    const foregroundTurnId = sessionId
      ? inFlightTurnIdBySessionRef.current.get(sessionId) || null
      : null;
    foregroundTurnIdRef.current = foregroundTurnId;
    setIsLoading(isRuntimeSessionLoading(sessionId));
    restoreForegroundTurnSnapshot(foregroundTurnId);
    emitResearchChatRuntimeChange();
    return () => {
      cancelPendingStreamUiCommit();
      if (researchChatRuntimeStore.foregroundSessionId === sessionId) {
        researchChatRuntimeStore.foregroundSessionId = null;
        emitResearchChatRuntimeChange();
      }
    };
  }, [cancelPendingStreamUiCommit, restoreForegroundTurnSnapshot, sessionId]);

  useEffect(() => {
    const persistedRunStatus = [...messages]
      .reverse()
      .find(message => message.runStatus)?.runStatus;
    if (persistedRunStatus) {
      setRuntimeRunStatus(sessionId, persistedRunStatus);
      setRunStatus(getRuntimeRunStatus(sessionId));
    }
  }, [messages, sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    void (async () => {
      try {
        const response = await apiCall({ action: 'get_session', sessionId });
        const data = (await response.json()) as SessionRecoveryResponse;
        if (!active || !response.ok || !data.runStatus) return;
        const current = getRuntimeRunStatus(sessionId);
        if (
          !current ||
          current.runId === data.runStatus.runId ||
          !isRuntimeSessionLoading(sessionId)
        ) {
          const next = setRuntimeRunStatus(sessionId, data.runStatus);
          if (activeSessionIdRef.current === sessionId) setRunStatus(next);
        }
      } catch {
        // Transcript hydration remains usable if the supplemental run read fails.
      }
    })();
    return () => {
      active = false;
    };
  }, [apiCall, sessionId]);

  useEffect(() => {
    if (runtimeOwnerMatches) {
      syncRuntimeMessageCountFromLiveSession(sessionId, messages.length);
    }
  }, [messages.length, runtimeOwnerMatches, sessionId]);

  useEffect(() => {
    if (!sessionId) return;

    const foregroundTurnId = inFlightTurnIdBySessionRef.current.get(sessionId) || null;
    const foregroundTurn = foregroundTurnId ? inFlightTurnsRef.current.get(foregroundTurnId) : null;
    if (foregroundTurn?.optimisticMessage) {
      ensureTurnMessages(
        sessionId,
        foregroundTurn.optimisticMessage.id,
        foregroundTurn.optimisticMessage,
        undefined,
        foregroundTurn.baselineMessageCount
      );
    }

    const pendingResults = getRuntimePendingTurnResults(sessionId);
    if (pendingResults.length === 0) return;

    let pendingResultsChanged = false;
    const nextPendingResults: PendingTurnResult[] = [];

    for (const pendingResult of pendingResults) {
      if (pendingResult.type === 'recovered') {
        if (pendingResult.metadataApplied) {
          pendingResultsChanged = true;
          continue;
        }
        setMessages(pendingResult.messages);
        if (pendingResult.updatedAt) {
          setSessionUpdatedAt(pendingResult.updatedAt);
        }
        if (pendingResult.contextUsage !== undefined) {
          setContextUsage(pendingResult.contextUsage || null);
        }
        nextPendingResults.length = 0;
        pendingResultsChanged = true;
        clearLiveStreamUi();
        continue;
      }

      if (pendingResult.type === 'completed') {
        ensureTurnMessages(
          sessionId,
          pendingResult.optimisticUserMessageId,
          pendingResult.userMessage,
          [...pendingResult.steerMessages, pendingResult.modelMessage],
          pendingResult.baselineMessageCount
        );
        if (!pendingResult.metadataApplied) {
          setSessionUpdatedAt(pendingResult.updatedAt);
          if (pendingResult.contextUsage !== undefined) {
            setContextUsage(pendingResult.contextUsage || null);
          }
          if (pendingResult.sessionName !== undefined) {
            setSessionName(pendingResult.sessionName || null);
          }
          nextPendingResults.push({
            ...pendingResult,
            metadataApplied: true,
          });
          pendingResultsChanged = true;
        } else {
          nextPendingResults.push(pendingResult);
        }
        continue;
      }

      if (
        hasPersistedModelTurn(
          messages,
          pendingResult.userMessage,
          pendingResult.baselineMessageCount,
          pendingResult.persistedUserMessageId,
          pendingResult.persistedModelMessageId
        )
      ) {
        pendingResultsChanged = true;
        continue;
      }

      ensureTurnMessages(
        sessionId,
        pendingResult.optimisticUserMessageId,
        pendingResult.userMessage,
        pendingResult.errorMessage,
        pendingResult.baselineMessageCount
      );
      if (!pendingResult.metadataApplied) {
        setSessionUpdatedAt(pendingResult.updatedAt);
      }
      if (!pendingResult.notified) {
        toast.error(pendingResult.errorText);
      }
      if (!pendingResult.notified || !pendingResult.metadataApplied) {
        nextPendingResults.push({
          ...pendingResult,
          notified: true,
          metadataApplied: true,
        });
        pendingResultsChanged = true;
      } else {
        nextPendingResults.push(pendingResult);
      }
    }

    if (pendingResultsChanged) {
      setRuntimePendingTurnResults(sessionId, nextPendingResults);
    }
  }, [
    ensureTurnMessages,
    clearLiveStreamUi,
    messages,
    runtimeRevision,
    sessionId,
    setContextUsage,
    setSessionName,
    setSessionUpdatedAt,
  ]);

  const enqueueMessage = useCallback(
    (
      content: string,
      images?: ImageAttachment[],
      selectedSkills?: SelectedAgentSkill[],
      executionMode: ResearchExecutionMode = 'standard',
      documents?: DocumentAttachment[],
      context?: DirectMessageContext
    ) => {
      if (!sessionId) return;
      const trimmed = content.trim();
      if (!trimmed && (!images || images.length === 0) && (!documents || documents.length === 0))
        return;
      const preview = trimmed || (documents?.length ? 'Document message' : 'Image message');
      const label = preview.length > 60 ? `${preview.slice(0, 60)}...` : preview;
      const queuedModelId = context?.targetModelId || selectedModelId || undefined;
      const queuedActiveReportId =
        context && Object.prototype.hasOwnProperty.call(context, 'targetActiveReportId')
          ? context?.targetActiveReportId
          : getActiveReportId?.() || null;
      const queuedReasoningEffort =
        context?.reasoningEffort ||
        (queuedModelId && reasoningEffortModelIds?.includes(queuedModelId)
          ? getStoredResearchReasoningEffort()
          : undefined);
      const websitePreviewContext = getWebsitePreviewContextSnapshot?.() || undefined;
      const websitePreviewContextRef = websitePreviewContext
        ? {
            previewId: websitePreviewContext.previewId,
            contextVersion: websitePreviewContext.contextVersion,
          }
        : getWebsitePreviewContextReference?.() || undefined;
      const canvasContext = getCanvasContextSnapshot?.() || undefined;
      const canvasContextRef = canvasContext
        ? {
            artifactId: canvasContext.artifactId,
            version: canvasContext.version,
            contextVersion: canvasContext.contextVersion,
          }
        : getCanvasContextReference?.() || undefined;
      const queuedCommand: RichScheduledCommand = {
        id: `scheduled-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: 'message',
        label,
        queuedAt: new Date().toISOString(),
        modelId: queuedModelId,
        message: trimmed,
        images,
        documents,
        selectedSkills,
        activeReportId: queuedActiveReportId,
        executionMode,
        turnId: `research-turn-${crypto.randomUUID()}`,
        ...(websitePreviewContext ? { websitePreviewContext } : {}),
        ...(websitePreviewContextRef ? { websitePreviewContextRef } : {}),
        ...(canvasContext ? { canvasContext } : {}),
        ...(canvasContextRef ? { canvasContextRef } : {}),
        ...(queuedReasoningEffort ? { reasoningEffort: queuedReasoningEffort } : {}),
      };
      setRuntimeScheduledCommands(sessionId, prev => [...prev, queuedCommand]);
      if (websitePreviewContext?.selection && websitePreviewContextRef) {
        void clearWebsitePreviewSelection?.(websitePreviewContextRef.contextVersion);
      }
      if (canvasContext?.selection && canvasContextRef) {
        clearCanvasSelection?.(canvasContextRef.contextVersion);
      }
      toast.success('Message queued');
    },
    [
      getActiveReportId,
      getWebsitePreviewContextSnapshot,
      getWebsitePreviewContextReference,
      clearWebsitePreviewSelection,
      getCanvasContextSnapshot,
      getCanvasContextReference,
      clearCanvasSelection,
      reasoningEffortModelIds,
      selectedModelId,
      sessionId,
    ]
  );

  const retainSteerInQueue = useCallback(
    (
      turnSessionId: string,
      steerId: string,
      options: SteerMessageOptions,
      turnContext?: Message
    ) => {
      const commandId = `retained-steer-${steerId}`;
      if (getRuntimeScheduledCommands(turnSessionId).some(command => command.id === commandId)) {
        return;
      }
      const message = options.content.trim();
      const hasTargetReport = Object.prototype.hasOwnProperty.call(options, 'targetActiveReportId');
      const activeReportId = hasTargetReport
        ? options.targetActiveReportId
        : turnContext?.activeReportId;
      const queuedCommand: RichScheduledCommand = {
        id: commandId,
        type: 'message',
        label:
          message.length > 0
            ? message.length > 60
              ? `${message.slice(0, 60)}...`
              : message
            : options.documents?.length
              ? 'Document steer'
              : options.images?.length
                ? 'Image steer'
                : 'Steering configuration',
        queuedAt: new Date().toISOString(),
        modelId: options.targetModelId || turnContext?.targetModelId,
        message,
        images: options.images,
        documents: options.documents,
        selectedSkills:
          options.selectedSkills === undefined
            ? turnContext?.selectedSkills
            : options.selectedSkills,
        activeReportId,
        executionMode: options.executionMode || turnContext?.executionMode || 'standard',
        turnId: `research-turn-${crypto.randomUUID()}`,
        ...(options.websitePreviewContext || turnContext?.websitePreviewContext
          ? {
              websitePreviewContext:
                options.websitePreviewContext || turnContext?.websitePreviewContext,
            }
          : {}),
        ...(options.websitePreviewContextRef || turnContext?.websitePreviewContextRef
          ? {
              websitePreviewContextRef:
                options.websitePreviewContextRef || turnContext?.websitePreviewContextRef,
            }
          : {}),
        ...(options.canvasContext || turnContext?.canvasContext
          ? { canvasContext: options.canvasContext || turnContext?.canvasContext }
          : {}),
        ...(options.canvasContextRef || turnContext?.canvasContextRef
          ? { canvasContextRef: options.canvasContextRef || turnContext?.canvasContextRef }
          : {}),
        ...(options.reasoningEffort
          ? { reasoningEffort: options.reasoningEffort }
          : turnContext?.reasoningEffort
            ? { reasoningEffort: turnContext.reasoningEffort }
            : {}),
      };
      if (!message && !options.images?.length && !options.documents?.length) {
        researchChatRuntimeStore.dequeueBlockedCommandIds.add(commandId);
      }
      setRuntimeScheduledCommands(turnSessionId, current => [...current, queuedCommand]);
      toast.info('The unfinished steer was kept in the queue.', {
        id: `research-steer-retained-${steerId}`,
      });
    },
    []
  );

  const cancelScheduledCommand = useCallback(
    (scheduledCommandId: string) => {
      const current = getRuntimeScheduledCommands(sessionId);
      const cancelled = current.find(command => command.id === scheduledCommandId);
      researchChatRuntimeStore.dequeueBlockedCommandIds.delete(scheduledCommandId);
      setRuntimeScheduledCommands(
        sessionId,
        current.filter(command => command.id !== scheduledCommandId)
      );
      cancelled?.documents?.forEach(document => {
        void deleteUploadedDocument(document).catch(() => undefined);
      });
    },
    [sessionId]
  );

  const clearScheduledCommands = useCallback(() => {
    const current = getRuntimeScheduledCommands(sessionId);
    current.forEach(command => {
      researchChatRuntimeStore.dequeueBlockedCommandIds.delete(command.id);
    });
    setRuntimeScheduledCommands(sessionId, []);
    current.forEach(command => {
      command.documents?.forEach(document => {
        void deleteUploadedDocument(document).catch(() => undefined);
      });
    });
  }, [sessionId]);

  const stopCurrentResponse = useCallback(() => {
    const turnId = foregroundTurnIdRef.current || getRuntimeInFlightTurnId(sessionId);
    if (!turnId) return;
    const turn = inFlightTurnsRef.current.get(turnId);
    if (!turn) return;
    turn.stoppedSnapshot ||= turnSnapshotsRef.current.get(turnId);
    if (!researchChatRuntimeStore.turnCancellationPromises.has(turnId)) {
      researchChatRuntimeStore.cancellingSessionIds.add(turn.sessionId);
      const cancellation = (async (): Promise<TurnCancellationResult> => {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const response = await apiCall({
              action: 'cancel_turn',
              sessionId: turn.sessionId,
              turnId,
            });
            if (response.ok) {
              const data = (await response.json()) as {
                turnStatus?: TurnCancellationResult;
              };
              const turnStatus =
                data.turnStatus === 'completed' ||
                data.turnStatus === 'cancelled' ||
                data.turnStatus === 'failed'
                  ? data.turnStatus
                  : null;
              if (turnStatus === 'completed') {
                researchChatRuntimeStore.persistenceConfirmedTurnIds.add(turnId);
              }
              return turnStatus;
            }
          } catch {
            // Retry the idempotent cancellation fence below.
          }
          await sleep(100 * (attempt + 1));
        }
        toast.error(
          'The response stopped locally, but server cancellation could not be confirmed.'
        );
        return null;
      })().finally(() => {
        researchChatRuntimeStore.cancellingSessionIds.delete(turn.sessionId);
        emitResearchChatRuntimeChange();
      });
      researchChatRuntimeStore.turnCancellationPromises.set(turnId, cancellation);
    }
    stoppedTurnIdsRef.current.add(turnId);
    if (foregroundTurnIdRef.current === turnId) {
      foregroundTurnIdRef.current = null;
    }
    turn.requestController.abort();
    cleanupStoppedTurnUi(turnId, turn.sessionId, turn.optimisticMessage.id);
    if (turn.reader) {
      void turn.reader.cancel('user_stop').catch(() => {
        // Ignore cancellation errors (reader may already be closed).
      });
    }
  }, [apiCall, cleanupStoppedTurnUi, sessionId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recoveryGenerationRef.current += 1;
      cancelPendingStreamUiCommit();
    };
  }, [cancelPendingStreamUiCommit]);

  useEffect(() => {
    const refreshFromRuntime = () => {
      if (!mountedRef.current) return;
      setActiveSessionIds(prev => {
        const next = getRuntimeActiveSessionIds();
        return arraysEqual(prev, next) ? prev : next;
      });
      setUnreadSessionIds(getRuntimeUnreadSessionIds());
      setScheduledCommands(getRuntimeScheduledCommands(sessionId));
      setRunStatus(getRuntimeRunStatus(sessionId));
      setLatestAppliedSteerReceipt(getRuntimeLatestAppliedSteerReceipt(sessionId));
      const foregroundTurnId = sessionId
        ? inFlightTurnIdBySessionRef.current.get(sessionId) || null
        : null;
      foregroundTurnIdRef.current = foregroundTurnId;
      setIsLoading(isRuntimeSessionLoading(sessionId));
      restoreForegroundTurnSnapshot(foregroundTurnId);
      setRuntimeRevision(current => current + 1);
    };

    researchChatRuntimeStore.subscribers.add(refreshFromRuntime);
    refreshFromRuntime();
    return () => {
      researchChatRuntimeStore.subscribers.delete(refreshFromRuntime);
    };
  }, [restoreForegroundTurnSnapshot, sessionId]);

  const recoverPersistedTurn = useCallback(
    async (
      recoverySessionId: string,
      turnId: string,
      baselineMessageCount: number,
      options: PersistedTurnRecoveryOptions = {}
    ): Promise<RecoveredPersistedTurn | null> => {
      const isCurrentRecovery = () =>
        inFlightTurnIdBySessionRef.current.get(recoverySessionId) === turnId &&
        (options.allowStoppedTurn || !stoppedTurnIdsRef.current.has(turnId));
      const deadline = Date.now() + (options.timeoutMs ?? STREAM_RECOVERY_TIMEOUT_MS);
      while (isCurrentRecovery() && Date.now() <= deadline) {
        try {
          const res = await apiCall({ action: 'get_session', sessionId: recoverySessionId });
          const data = (await res.json()) as SessionRecoveryResponse;
          if (!isCurrentRecovery()) return null;
          if (res.ok && data.session?.messages) {
            if (data.runStatus) {
              const next = setRuntimeRunStatus(recoverySessionId, data.runStatus);
              if (activeSessionIdRef.current === recoverySessionId) setRunStatus(next);
            }
            const serverMessages = hydrateMessageSources(data.session.messages);
            const hasNewModelTurn =
              serverMessages.length > baselineMessageCount &&
              serverMessages
                .slice(baselineMessageCount)
                .some(
                  message =>
                    message.role === 'model' &&
                    (message.content.trim().length > 0 ||
                      Boolean(message.runStatus) ||
                      Boolean(message.runReceipt))
                );

            if (hasNewModelTurn) {
              if (!isCurrentRecovery()) return null;
              return {
                messages: serverMessages,
                updatedAt: data.session.updatedAt,
                contextUsage: data.session.contextUsage,
              };
            }
          }
        } catch {
          // Keep polling; transient session reads are expected while the server turn is still finishing.
        }

        await sleep(options.pollMs ?? STREAM_RECOVERY_POLL_MS);
      }

      return null;
    },
    [apiCall]
  );

  const applyRecoveredTurn = useCallback(
    (
      turnSessionId: string,
      turnId: string,
      recovered: RecoveredPersistedTurn,
      options: { restoreCurrentSession?: boolean } = {}
    ) => {
      const recoveredPendingResult: PendingTurnResult = {
        type: 'recovered',
        messages: recovered.messages,
        updatedAt: recovered.updatedAt,
        contextUsage: recovered.contextUsage,
        metadataApplied: false,
      };
      setRuntimeMessageCount(turnSessionId, recovered.messages.length);
      const restoreCurrentSession =
        isForegroundTurn(turnId, turnSessionId) ||
        Boolean(
          options.restoreCurrentSession &&
            mountedRef.current &&
            activeSessionIdRef.current === turnSessionId
        );
      if (restoreCurrentSession) {
        setMessages(recovered.messages);
        if (recovered.updatedAt) {
          setSessionUpdatedAt(recovered.updatedAt);
        }
        if (recovered.contextUsage !== undefined) {
          setContextUsage(recovered.contextUsage || null);
        }
        setRuntimePendingTurnResults(turnSessionId, []);
        if (isForegroundTurn(turnId, turnSessionId)) {
          clearTurnLiveState(turnId, turnSessionId);
        } else {
          clearLiveStreamUi();
        }
      } else {
        appendRuntimePendingTurnResult(turnSessionId, recoveredPendingResult);
        markSessionUnread(turnSessionId);
      }
    },
    [
      clearLiveStreamUi,
      clearTurnLiveState,
      isForegroundTurn,
      markSessionUnread,
      setContextUsage,
      setMessages,
      setSessionUpdatedAt,
    ]
  );

  // Handle SSE streaming
  const handleStream = useCallback(
    async (res: Response, turnId: string, turnSessionId: string, originalRequest?: Record<string, unknown>) => {
      const admissionPending = res.headers.get('x-attune-agent-admission-pending') === '1';
      const resilient = resumableGatewayResponse(res, (after, signal) => apiCall(admissionPending && originalRequest ? originalRequest : {
        action: 'gateway_resume', sessionId: turnSessionId, turnId, after,
      }, { signal }), inFlightTurnsRef.current.get(turnId)?.requestController.signal);
      const reader = resilient.body?.getReader();
      const turn = inFlightTurnsRef.current.get(turnId);
      if (turn) {
        turn.reader = reader ?? null;
        turn.steeringReady = !admissionPending;
      }
      const decoder = new TextDecoder();
      let fullText = '';
      let thinkingAccumulator = '';
      let buffer = '';
      let isFirstChunk = true;
      let aborted = false;
      let streamError: string | null = null;
      const toolUsageMap = new Map<string, ToolStatus>();
      // Track tool call order for consistent display
      const toolCallOrder: string[] = [];
      // Accumulate ALL sources across all tool calls (not just per-tool)
      const allSourcesMap = new Map<string, SourceReference>();
      let sourceIdCounter = 0; // Fallback counter for sources without unique identifiers
      const timelineAccumulator: TraceTimelineItem[] = [];
      const toolTimelineIndexMap = new Map<string, number>();
      let timelineFlushTimer: ReturnType<typeof setTimeout> | null = null;
      let lastTimelineFlushAt = 0;
      let timelineDirty = false;
      let persistedTurn:
        | {
            userMessageId?: string;
            modelMessageId?: string;
            sessionName?: string | null;
            skillUsage?: AgentSkillUsage[];
            executionMode?: ResearchExecutionMode;
            runStatus?: ResearchRunStatus;
            runReceipt?: ResearchRunReceipt;
            steerMessages?: Message[];
          }
        | undefined;
      let streamContextUsage: ContextUsage | null | undefined;
      let latestRunStatus: ResearchRunStatus | null = null;
      let childStatuses: SubagentStatusRow[] = [];
      let rootStopped = false;

      const isStopRequested = () => stoppedTurnIdsRef.current.has(turnId);

      if (isStopRequested()) {
        aborted = true;
        if (reader) {
          await reader.cancel('owner_reset').catch(() => {});
        }
        return {
          fullText,
          toolUsage: [],
          sources: [],
          thinkingText: thinkingAccumulator,
          traceTimeline: [],
          persistedTurn,
          contextUsage: streamContextUsage,
          runStatus: latestRunStatus,
          aborted,
          streamError,
        };
      }

      const updateActiveTools = (updater: (tools: ToolStatus[]) => ToolStatus[]) => {
        const previous = turnSnapshotsRef.current.get(turnId)?.activeTools || [];
        const next = updater(previous);
        applyTurnSnapshot(turnId, turnSessionId, { activeTools: next });
      };

      const clearTimelineFlushTimer = () => {
        if (timelineFlushTimer !== null) {
          clearTimeout(timelineFlushTimer);
          timelineFlushTimer = null;
        }
      };

      const flushTimelineNow = () => {
        clearTimelineFlushTimer();
        if (!timelineDirty) {
          return;
        }
        timelineDirty = false;
        lastTimelineFlushAt = Date.now();
        applyTurnSnapshot(turnId, turnSessionId, {
          streamTimeline: [...timelineAccumulator],
        });
      };

      const scheduleTimelineFlush = () => {
        timelineDirty = true;
        const elapsedMs = Date.now() - lastTimelineFlushAt;
        if (elapsedMs >= TIMELINE_FLUSH_INTERVAL_MS) {
          flushTimelineNow();
          return;
        }
        if (timelineFlushTimer !== null) {
          return;
        }
        timelineFlushTimer = setTimeout(flushTimelineNow, TIMELINE_FLUSH_INTERVAL_MS - elapsedMs);
      };

      // Helper: Generate unique key for a tool call.
      // Prefer server-provided callId when present (for parallel identical calls).
      const getToolKey = (
        tool: string,
        args?: Record<string, unknown>,
        callId?: string
      ): string => {
        if (callId) {
          return `call:${callId}`;
        }
        if (!args || Object.keys(args).length === 0) {
          return tool;
        }
        // Create a stable key from the args (query, engine, etc.)
        const argStr = Object.entries(args)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([k, v]) => `${k}:${v}`)
          .join('|');
        return `${tool}::${argStr}`;
      };

      const pushReasoningChunk = (chunkText: string) => {
        if (!chunkText) return;
        const lastItem = timelineAccumulator[timelineAccumulator.length - 1];
        if (lastItem?.type === 'reasoning') {
          lastItem.content += chunkText;
        } else {
          timelineAccumulator.push({
            type: 'reasoning',
            id: `reasoning-${timelineAccumulator.length + 1}`,
            content: chunkText,
          });
        }
        scheduleTimelineFlush();
      };

      const upsertToolTimeline = (toolKey: string, tool: ToolStatus) => {
        const timelineItem: TraceTimelineItem = {
          type: 'tool',
          id: toolKey,
          ...tool,
        };
        const existingIndex = toolTimelineIndexMap.get(toolKey);
        if (
          existingIndex !== undefined &&
          timelineAccumulator[existingIndex] &&
          timelineAccumulator[existingIndex].type === 'tool'
        ) {
          timelineAccumulator[existingIndex] = timelineItem;
        } else {
          toolTimelineIndexMap.set(toolKey, timelineAccumulator.length);
          timelineAccumulator.push(timelineItem);
        }
        scheduleTimelineFlush();
      };

      // Reset tool status and thinking text
      applyTurnSnapshot(turnId, turnSessionId, {
        activeTools: [],
        thinkingText: '',
        streamTimeline: [],
      });

      if (reader) {
        try {
          if (isStopRequested()) {
            aborted = true;
            await reader.cancel('user_stop').catch(() => {});
          }

          while (!aborted && !streamError) {
            const { done, value } = await reader.read();
            if (done) break;

            if (isStopRequested()) {
              aborted = true;
              await reader.cancel('user_stop').catch(() => {});
              break;
            }

            // Append new chunk to buffer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            // Detect HTML error pages early (DOCTYPE, html tags)
            if (isFirstChunk) {
              isFirstChunk = false;
              const lowerChunk = chunk.toLowerCase();
              if (lowerChunk.includes('<!doctype') || lowerChunk.includes('<html')) {
                console.error('[Stream] Received HTML instead of SSE:', chunk.substring(0, 500));
                throw new Error(
                  'Server returned an error page. This may be a timeout - please try a shorter query.'
                );
              }
            }

            // Process complete lines (SSE events are separated by \n\n)
            const events = buffer.split('\n\n');
            // Keep the last potentially incomplete event in the buffer
            buffer = events.pop() || '';

            if (process.env.NODE_ENV === 'development' && events.length > 0) {
              console.debug(
                '[Stream] Processing',
                events.length,
                'events, buffer remaining:',
                buffer.length
              );
            }

            for (const event of events) {
              const lines = event.split('\n');
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  const data = line.slice(6);
                  if (data === '[DONE]') {
                    continue;
                  }
                  try {
                    const parsed = JSON.parse(data);
                    if (process.env.NODE_ENV === 'development') {
                      console.debug(
                        '[Stream] Parsed event:',
                        parsed.type,
                        parsed.tool || parsed.text?.substring(0, 50)
                      );
                    }

                    // Handle different event types
                    if (isChildExecutionEvent(parsed)) continue;
                    if (parsed.type === 'response_phase') {
                      const phase = parseResearchResponsePhase(parsed.responsePhase);
                      if (phase) applyTurnSnapshot(turnId, turnSessionId, { responsePhase: phase });
                      continue;
                    }
                    if (parsed.type === 'subagent_status') {
                      childStatuses = upsertSubagentStatus(childStatuses, parsed.subagent);
                      if (rootStopped) childStatuses = cancelActiveSubagentStatuses(childStatuses);
                      applyTurnSnapshot(turnId, turnSessionId, { subagents: childStatuses });
                      continue;
                    }
                    if (parsed.type === 'gateway_status') {
                      if (parsed.phase === 'cancelled' || parsed.phase === 'failed') {
                        rootStopped = true;
                        childStatuses = cancelActiveSubagentStatuses(childStatuses);
                        applyTurnSnapshot(turnId, turnSessionId, { subagents: childStatuses, responsePhase: null });
                      }
                      if (turn) turn.steeringReady = ['queued', 'running', 'recovering', 'waiting_for_subagents'].includes(parsed.phase);
                      const statusText = parsed.phase === 'queued' ? 'Queued — waiting for execution capacity.'
                        : parsed.phase === 'recovering' ? 'Recovering saved progress…'
                        : parsed.phase === 'waiting_for_subagents' ? 'Waiting for agents.' : thinkingAccumulator;
                      applyTurnSnapshot(turnId, turnSessionId, { thinkingText: statusText });
                      continue;
                    }
                    if (parsed.type === 'gateway_reset') {
                      const snapshot = parsed.snapshot || {};
                      fullText = snapshot.fullText || '';
                      thinkingAccumulator = snapshot.thinkingText || '';
                      childStatuses = reconcileSubagentStatuses(childStatuses, snapshot.subagents);
                      if (rootStopped) childStatuses = cancelActiveSubagentStatuses(childStatuses);
                      toolUsageMap.clear();
                      toolCallOrder.length = 0;
                      allSourcesMap.clear();
                      timelineAccumulator.length = 0;
                      toolTimelineIndexMap.clear();
                      for (const [key, item] of snapshot.toolUsageEntries || []) {
                        toolUsageMap.set(key, item);
                        toolCallOrder.push(key);
                        for (const source of item.sources || []) allSourcesMap.set(getSourceDeduplicationKey(source), source);
                      }
                      timelineAccumulator.push(...(snapshot.traceTimeline || []));
                      for (const [key, index] of snapshot.toolTimelineIndexEntries || []) toolTimelineIndexMap.set(key, index);
                      applyTurnSnapshot(turnId, turnSessionId, {
                        streamingText: fullText, thinkingText: thinkingAccumulator,
                        responsePhase: parseResearchResponsePhase(snapshot.responsePhase),
                        activeTools: [...toolUsageMap.values()], streamTimeline: [...timelineAccumulator],
                        subagents: childStatuses,
                      });
                      continue;
                    }
                    if (parsed.type === 'preview_capture_request' && parsed.request) {
                      const request = parsed.request as PreviewCaptureRequest;
                      if (request.turnId !== turnId || request.sessionId !== turnSessionId)
                        continue;
                      void (async () => {
                        const deadline = performance.now() + PREVIEW_CAPTURE_TIMEOUT_MS;
                        const result: PreviewCaptureResult = isForegroundTurn(turnId, turnSessionId)
                          ? await requestPreviewCapture(request, turn?.requestController.signal)
                          : {
                              status: 'unavailable',
                              error: 'This task is no longer open in the editor.',
                            };
                        const payload = {
                          action: 'preview_capture_result',
                          sessionId: turnSessionId,
                          turnId,
                          captureRequestId: request.requestId,
                          captureResult: result,
                        };
                        for (
                          let attempt = 0;
                          attempt < 2 && (attempt === 0 || performance.now() < deadline);
                          attempt++
                        ) {
                          try {
                            const response = await apiCall(payload, {
                              signal: turn?.requestController.signal,
                            });
                            if (response.ok || response.status < 500) return;
                          } catch {
                            if (turn?.requestController.signal.aborted) return;
                          }
                        }
                      })().catch(() => {
                        /* The server returns a bounded capture timeout. */
                      });
                    } else if (parsed.type === 'steer_applied') {
                      applyTurnSnapshot(turnId, turnSessionId, { responsePhase: 'researching' });
                      const appliedSequence =
                        typeof parsed.steerSequence === 'number' ? parsed.steerSequence : undefined;
                      const appliedContext = parseSteerContextSnapshot(parsed.steerContext);
                      if (appliedContext && appliedSequence !== undefined) {
                        applyResolvedSteerContext(turnId, appliedContext, appliedSequence);
                      }
                      const appliedSteerIds = Array.from(
                        new Set([
                          ...(typeof parsed.steerId === 'string' ? [parsed.steerId] : []),
                          ...(Array.isArray(parsed.steerIds)
                            ? parsed.steerIds.filter(
                                (candidate: unknown): candidate is string =>
                                  typeof candidate === 'string'
                              )
                            : []),
                        ])
                      );
                      if (typeof parsed.modelId === 'string' && parsed.modelId) {
                        appliedModelIdByTurnRef.current.set(turnId, parsed.modelId);
                      }
                      if (appliedSteerIds.length === 0) {
                        if (isForegroundTurn(turnId, turnSessionId)) {
                          toast.success('Steer applied.');
                        }
                      } else {
                        appliedSteerIds.forEach(appliedSteerId => {
                          const receiptState = steerReceiptStateByIdRef.current.get(appliedSteerId);
                          if (receiptState?.applied) return;
                          pendingSteerIdsByTurnRef.current.get(turnId)?.delete(appliedSteerId);
                          const pendingOptions =
                            pendingSteerOptionsByIdRef.current.get(appliedSteerId);
                          if (pendingOptions && !appliedContext) {
                            applyAcceptedSteerContext(turnId, pendingOptions, appliedSequence);
                          }
                          const ambiguousDirectSteers =
                            researchChatRuntimeStore.ambiguousDirectSteerBySession.get(
                              turnSessionId
                            );
                          if (ambiguousDirectSteers) {
                            for (const [signature, ambiguousSteer] of ambiguousDirectSteers) {
                              if (ambiguousSteer.steerId === appliedSteerId) {
                                ambiguousDirectSteers.delete(signature);
                              }
                            }
                            if (ambiguousDirectSteers.size === 0) {
                              researchChatRuntimeStore.ambiguousDirectSteerBySession.delete(
                                turnSessionId
                              );
                            }
                          }
                          const scheduledCommands = getRuntimeScheduledCommands(turnSessionId);
                          if (
                            scheduledCommands.some(
                              command => getScheduledCommandSteerId(command) === appliedSteerId
                            )
                          ) {
                            setRuntimeScheduledCommands(
                              turnSessionId,
                              scheduledCommands.filter(
                                command => getScheduledCommandSteerId(command) !== appliedSteerId
                              )
                            );
                          }
                          steerReceiptStateByIdRef.current.set(appliedSteerId, {
                            turnId,
                            applied: true,
                            postAccepted: receiptState?.postAccepted || false,
                            ...(appliedSequence !== undefined ? { sequence: appliedSequence } : {}),
                          });
                          toast.success('Steer applied.', {
                            id: getSteerToastId(appliedSteerId),
                          });
                          setRuntimeLatestAppliedSteerReceipt(turnSessionId, {
                            steerId: appliedSteerId,
                            turnId,
                            ...(appliedSequence !== undefined ? { sequence: appliedSequence } : {}),
                          });
                        });
                      }
                    } else if (parsed.type === 'run_status' && parsed.runStatus) {
                      const candidate = parsed.runStatus as ResearchRunStatus;
                      if (
                        typeof candidate.runId === 'string' &&
                        typeof candidate.sequence === 'number'
                      ) {
                        latestRunStatus = setRuntimeRunStatus(turnSessionId, candidate);
                        if (isForegroundTurn(turnId, turnSessionId)) {
                          setRunStatus(latestRunStatus);
                        }
                      }
                    } else if (parsed.type === 'thinking') {
                      // Keepalive heartbeat — no content
                    } else if (
                      (parsed.type === 'progress' || parsed.type === 'progress_update') &&
                      typeof parsed.progressText === 'string' &&
                      parsed.progressText.trim()
                    ) {
                      upsertProgressTimeline(
                        timelineAccumulator, parsed.progressText,
                        typeof parsed.progressId === 'string' ? parsed.progressId : undefined
                      );
                      scheduleTimelineFlush();
                    } else if (parsed.type === 'thinking_text' && parsed.text) {
                      thinkingAccumulator += parsed.text;
                      applyTurnSnapshot(turnId, turnSessionId, {
                        thinkingText: thinkingAccumulator,
                      });
                      pushReasoningChunk(parsed.text);
                    } else if (parsed.type === 'tool_call' && parsed.tool) {
                      applyTurnSnapshot(turnId, turnSessionId, { responsePhase: 'researching' });
                      if (process.env.NODE_ENV === 'development') {
                        console.debug('[Stream] Tool call:', parsed.tool, parsed.args);
                      }
                      const parsedCallId = parsed.callId as string | undefined;
                      const toolKey = getToolKey(parsed.tool, parsed.args, parsedCallId);
                      const executionMode = parsed.executionMode as
                        | ToolStatus['executionMode']
                        | undefined;
                      const subAgent = parsed.subAgent as ToolStatus['subAgent'] | undefined;
                      const pendingStatus: ToolStatus['status'] =
                        parsed.status === 'preparing' ? 'preparing' : 'calling';
                      const routedCall =
                        parsed.tool === 'tools.call' || parsed.tool === 'run_attune_data_tool';
                      const callArgs =
                        parsed.args &&
                        typeof parsed.args === 'object' &&
                        !Array.isArray(parsed.args)
                          ? (parsed.args as Record<string, unknown>)
                          : {};
                      const canvasTool = routedCall
                        ? String(callArgs.toolName ?? callArgs.tool ?? '').trim()
                        : parsed.tool;
                      const canvasArgs = routedCall ? callArgs.arguments : callArgs;
                      const canvasId =
                        canvasArgs &&
                        typeof canvasArgs === 'object' &&
                        !Array.isArray(canvasArgs) &&
                        typeof (canvasArgs as Record<string, unknown>).canvasId === 'string'
                          ? String((canvasArgs as Record<string, unknown>).canvasId).trim()
                          : '';
                      if (
                        pendingStatus === 'calling' &&
                        canvasId &&
                        (canvasTool === 'canvas.apply_patch' || canvasTool === 'canvas.restore')
                      ) {
                        window.dispatchEvent(
                          new CustomEvent(CANVAS_LIVE_EDIT_STARTED_EVENT, {
                            detail: { canvasId },
                          })
                        );
                      }
                      const nextToolStatus: ToolStatus = {
                        callId: parsedCallId,
                        tool: parsed.tool,
                        status: pendingStatus,
                        args: parsed.args,
                        executionMode,
                        subAgent,
                      };
                      toolUsageMap.set(toolKey, nextToolStatus);
                      upsertToolTimeline(toolKey, nextToolStatus);
                      if (!toolCallOrder.includes(toolKey)) {
                        toolCallOrder.push(toolKey);
                      }
                      // Append new tool call, preserving all previous calls
                      updateActiveTools(prev => {
                        // Check if this exact tool+args combo already exists
                        const existingIdx = prev.findIndex(
                          t => getToolKey(t.tool, t.args, t.callId) === toolKey
                        );
                        if (existingIdx >= 0) {
                          // Update existing entry
                          const updated = [...prev];
                          updated[existingIdx] = {
                            callId: parsedCallId,
                            tool: parsed.tool,
                            status: pendingStatus,
                            args: parsed.args,
                            executionMode,
                            subAgent,
                          };
                          return updated;
                        }
                        // Append new entry (preserve full log)
                        return [
                          ...prev,
                          {
                            callId: parsedCallId,
                            tool: parsed.tool,
                            status: pendingStatus,
                            args: parsed.args,
                            executionMode,
                            subAgent,
                          },
                        ];
                      });
                    } else if (parsed.type === 'tool_result' && parsed.tool) {
                      if (process.env.NODE_ENV === 'development') {
                        console.debug(
                          '[Stream] Tool result:',
                          parsed.tool,
                          parsed.status,
                          parsed.count,
                          'sources:',
                          parsed.sources?.length || 0
                        );
                      }

                      // Accumulate sources from this tool call into allSourcesMap
                      const newSources = parsed.sources as SourceReference[] | undefined;
                      if (process.env.NODE_ENV === 'development') {
                        console.debug(
                          '[Stream] Sources from',
                          parsed.tool,
                          ':',
                          newSources?.map(s => s.type)
                        );
                      }
                      if (newSources) {
                        for (const src of newSources) {
                          const key = getSourceDeduplicationKey(src, sourceIdCounter);
                          // Increment counter if we used it (key contains 'uniq:')
                          if (key.includes('uniq:')) {
                            sourceIdCounter++;
                          }
                          if (!allSourcesMap.has(key)) {
                            allSourcesMap.set(key, src);
                          }
                        }
                      }

                      const parsedArgs = parsed.args as Record<string, unknown> | undefined;
                      const parsedExecutionMode = parsed.executionMode as
                        | ToolStatus['executionMode']
                        | undefined;
                      const parsedSubAgent = parsed.subAgent as ToolStatus['subAgent'] | undefined;
                      const parsedCallId = parsed.callId as string | undefined;
                      const keyedMatch = getToolKey(parsed.tool, parsedArgs, parsedCallId);
                      const keyedEntry = toolUsageMap.get(keyedMatch);
                      const matchingKey =
                        (keyedEntry &&
                        keyedEntry.tool === parsed.tool &&
                        (keyedEntry.status === 'preparing' || keyedEntry.status === 'calling')
                          ? keyedMatch
                          : toolCallOrder.findLast(key => {
                              const entry = toolUsageMap.get(key);
                              if (
                                !entry ||
                                entry.tool !== parsed.tool ||
                                (entry.status !== 'preparing' && entry.status !== 'calling')
                              ) {
                                return false;
                              }
                              if (!parsedCallId) return true;
                              return entry.callId === parsedCallId;
                            })) || keyedMatch;

                      // Get existing tool entry to preserve args
                      const existing = toolUsageMap.get(matchingKey);

                      const nextToolStatus: ToolStatus = {
                        callId: existing?.callId || parsedCallId,
                        tool: parsed.tool,
                        status: parsed.status,
                        count: typeof parsed.count === 'number' ? parsed.count : undefined,
                        error: parsed.error,
                        resultState: parsed.resultState,
                        failureKind: parsed.failureKind,
                        fallbackUsed: parsed.fallbackUsed,
                        coverageGap: parsed.coverageGap,
                        metadata: parsed.metadata,
                        args: existing?.args || parsedArgs,
                        sources: parsed.sources,
                        executionMode: existing?.executionMode || parsedExecutionMode,
                        subAgent: existing?.subAgent || parsedSubAgent,
                      };
                      const completedTool =
                        typeof parsed.metadata?.attuneTool === 'string'
                          ? parsed.metadata.attuneTool
                          : parsed.tool;
                      if (
                        parsed.status === 'done' &&
                        (completedTool === 'design_system.apply_patch' ||
                          completedTool === 'design_system.restore')
                      ) {
                        window.dispatchEvent(new Event(PROJECT_DESIGN_SYSTEM_UPDATED_EVENT));
                      }
                      toolUsageMap.set(matchingKey, nextToolStatus);
                      upsertToolTimeline(matchingKey, nextToolStatus);

                      updateActiveTools(prev => {
                        const exactIdx = prev.findIndex(
                          t =>
                            getToolKey(t.tool, t.args, t.callId) === matchingKey &&
                            (t.status === 'preparing' || t.status === 'calling')
                        );
                        if (exactIdx >= 0) {
                          const updated = [...prev];
                          updated[exactIdx] = {
                            ...updated[exactIdx],
                            callId: updated[exactIdx].callId || parsedCallId,
                            status: parsed.status,
                            count: typeof parsed.count === 'number' ? parsed.count : undefined,
                            error: parsed.error,
                            resultState: parsed.resultState,
                            failureKind: parsed.failureKind,
                            fallbackUsed: parsed.fallbackUsed,
                            coverageGap: parsed.coverageGap,
                            metadata: parsed.metadata,
                            sources: parsed.sources,
                          };
                          return updated;
                        }

                        // Backwards-compatible fallback for payloads without args.
                        const callingIdx = prev.findLastIndex(t => {
                          if (
                            t.tool !== parsed.tool ||
                            (t.status !== 'preparing' && t.status !== 'calling')
                          ) {
                            return false;
                          }
                          if (!parsedCallId) return true;
                          return t.callId === parsedCallId;
                        });
                        if (callingIdx >= 0) {
                          const updated = [...prev];
                          updated[callingIdx] = {
                            ...updated[callingIdx],
                            callId: updated[callingIdx].callId || parsedCallId,
                            status: parsed.status,
                            count: typeof parsed.count === 'number' ? parsed.count : undefined,
                            error: parsed.error,
                            resultState: parsed.resultState,
                            failureKind: parsed.failureKind,
                            fallbackUsed: parsed.fallbackUsed,
                            coverageGap: parsed.coverageGap,
                            metadata: parsed.metadata,
                            sources: parsed.sources,
                          };
                          return updated;
                        }
                        return prev;
                      });
                    } else if (parsed.type === 'text' && parsed.text) {
                      fullText += parsed.text;
                      applyTurnSnapshot(turnId, turnSessionId, { streamingText: fullText });
                    } else if (parsed.type === 'context_usage') {
                      // Update context usage from server.
                      if (parsed.contextUsage) {
                        streamContextUsage = parsed.contextUsage as ContextUsage;
                        if (isForegroundTurn(turnId, turnSessionId)) {
                          setContextUsage(streamContextUsage);
                        }
                      }
                      // Persisted turn IDs let us reconcile optimistic rows with
                      // server-authored message IDs for targeted exports.
                      if (parsed.persistedTurn && typeof parsed.persistedTurn === 'object') {
                        persistedTurn = {
                          userMessageId:
                            typeof parsed.persistedTurn.userMessageId === 'string'
                              ? parsed.persistedTurn.userMessageId
                              : undefined,
                          modelMessageId:
                            typeof parsed.persistedTurn.modelMessageId === 'string'
                              ? parsed.persistedTurn.modelMessageId
                              : undefined,
                          sessionName:
                            typeof parsed.persistedTurn.sessionName === 'string'
                              ? parsed.persistedTurn.sessionName
                              : parsed.persistedTurn.sessionName === null
                                ? null
                                : undefined,
                          skillUsage: Array.isArray(parsed.persistedTurn.skillUsage)
                            ? (parsed.persistedTurn.skillUsage as AgentSkillUsage[])
                            : undefined,
                          executionMode:
                            parsed.persistedTurn.executionMode === 'plan' ||
                            parsed.persistedTurn.executionMode === 'goal'
                              ? parsed.persistedTurn.executionMode
                              : parsed.persistedTurn.executionMode === 'standard'
                                ? 'standard'
                                : undefined,
                          runStatus: parsed.persistedTurn.runStatus as
                            | ResearchRunStatus
                            | undefined,
                          runReceipt: parsed.persistedTurn.runReceipt as
                            | ResearchRunReceipt
                            | undefined,
                          steerMessages: Array.isArray(parsed.persistedTurn.steerMessages)
                            ? (parsed.persistedTurn.steerMessages as Message[])
                            : undefined,
                        };
                        if (persistedTurn.runStatus) {
                          latestRunStatus = setRuntimeRunStatus(
                            turnSessionId,
                            persistedTurn.runStatus
                          );
                          if (isForegroundTurn(turnId, turnSessionId)) {
                            setRunStatus(latestRunStatus);
                          }
                        }
                      }
                    } else if (parsed.type === 'error' && parsed.error) {
                      childStatuses = cancelActiveSubagentStatuses(childStatuses);
                      applyTurnSnapshot(turnId, turnSessionId, { subagents: childStatuses });
                      streamError = String(parsed.error);
                      await reader.cancel('server_stream_error').catch(() => {});
                      break;
                    } else if (parsed.text) {
                      // Backwards compat: handle old format
                      fullText += parsed.text;
                      applyTurnSnapshot(turnId, turnSessionId, { streamingText: fullText });
                    }
                  } catch (e) {
                    if (e instanceof Error && !e.message.includes('JSON')) {
                      throw e;
                    }
                    console.warn(
                      '[Stream] JSON parse error, skipping line:',
                      line.substring(0, 100)
                    );
                  }
                }
              }
            }
          }
        } catch (streamError) {
          clearTimelineFlushTimer();
          if (!isStopRequested()) {
            throw streamError;
          }
          aborted = true;
        } finally {
          const currentTurn = inFlightTurnsRef.current.get(turnId);
          if (currentTurn?.reader === reader) {
            currentTurn.reader = null;
          }
        }
      }

      const toolUsage = Array.from(toolUsageMap.values()).map(finalizePendingToolStatus);

      // Use accumulated sources from ALL tool calls (already deduplicated)
      const sources = Array.from(allSourcesMap.values());
      if (process.env.NODE_ENV === 'development') {
        console.debug('[Stream] Final sources:', sources.length, 'by type:', {
          citations: sources.filter(s => s.type === 'citation').length,
          answers: sources.filter(s => s.type === 'answer').length,
          pages: sources.filter(s => s.type === 'page').length,
        });
      }

      const finalizedTimeline = timelineAccumulator
        .filter(item => (item.type === 'reasoning' ? item.content.trim().length > 0 : true))
        .map(item => (item.type === 'tool' ? finalizePendingToolStatus(item) : item));
      flushTimelineNow();
      clearTimelineFlushTimer();

      return {
        fullText,
        toolUsage,
        sources,
        thinkingText: thinkingAccumulator,
        traceTimeline: finalizedTimeline,
        persistedTurn,
        contextUsage: streamContextUsage,
        runStatus: latestRunStatus,
        subagents: childStatuses,
        aborted,
        streamError,
      };
    },
    [
      applyAcceptedSteerContext,
      applyResolvedSteerContext,
      applyTurnSnapshot,
      isForegroundTurn,
      setContextUsage,
      apiCall,
    ]
  );

  // Internal: Send message with explicit content (used by sendMessage and retryMessage)
  const sendMessageInternal = useCallback(
    async (options: SendMessageOptions): Promise<boolean> => {
      const {
        content,
        images,
        documents,
        selectedSkills,
        targetSessionId,
        targetModelId,
        targetActiveReportId,
        executionMode = 'standard',
        runId,
        runAction,
        expectedSequence,
        reasoningEffort,
        restoreDocumentsOnReject = false,
        suppressPreStreamFailureTurn = false,
        websitePreviewContext: suppliedWebsitePreviewContext,
        websitePreviewContextRef: suppliedWebsitePreviewContextRef,
        canvasContext: suppliedCanvasContext,
        canvasContextRef: suppliedCanvasContextRef,
      } = options;
      const turnSessionId = targetSessionId || sessionId;
      if (
        !turnSessionId ||
        (!content.trim() &&
          (!images || images.length === 0) &&
          !(options.resumeGatewayTurn && options.resumeImageAttachments?.length) &&
          (!documents || documents.length === 0))
      )
        return false;

      const isLocalSession = mountedRef.current && activeSessionIdRef.current === turnSessionId;
      const requestController = new AbortController();
      const userMsg = content.trim();
      const baselineMessageCount = isLocalSession
        ? messages.length
        : getRuntimeMessageCount(turnSessionId);
      const turnModelId = targetModelId || selectedModelId || undefined;
      const turnReasoningEffort =
        reasoningEffort ||
        (!options.resumeGatewayTurn && turnModelId && reasoningEffortModelIds?.includes(turnModelId)
          ? getStoredResearchReasoningEffort()
          : undefined);
      const turnActiveReportId =
        targetActiveReportId === undefined ? getActiveReportId?.() || null : targetActiveReportId;
      const activeReportSelectionBaseline = turnActiveReportId;
      const websitePreviewContext = options.resumeGatewayTurn ? suppliedWebsitePreviewContext :
        suppliedWebsitePreviewContext || getWebsitePreviewContextSnapshot?.() || undefined;
      const websitePreviewContextRef = options.resumeGatewayTurn ? suppliedWebsitePreviewContextRef :
        suppliedWebsitePreviewContextRef ||
        (websitePreviewContext
          ? {
              previewId: websitePreviewContext.previewId,
              contextVersion: websitePreviewContext.contextVersion,
            }
          : getWebsitePreviewContextReference?.() || undefined);
      const canvasContext = options.resumeGatewayTurn ? suppliedCanvasContext : suppliedCanvasContext || getCanvasContextSnapshot?.() || undefined;
      const canvasContextRef = options.resumeGatewayTurn ? suppliedCanvasContextRef :
        suppliedCanvasContextRef ||
        (canvasContext
          ? {
              artifactId: canvasContext.artifactId,
              version: canvasContext.version,
              contextVersion: canvasContext.contextVersion,
            }
          : getCanvasContextReference?.() || undefined);
      const userMessageTimestamp = new Date().toISOString();
      const tempUserMessageId = `temp-${Date.now()}`;
      const turnId = options.turnId || `research-turn-${crypto.randomUUID()}`;
      const optimisticMessage: Message = {
        id: tempUserMessageId,
        role: 'user',
        content: userMsg,
        timestamp: userMessageTimestamp,
        targetModelId: turnModelId,
        reasoningEffort: turnReasoningEffort || null,
        images,
        imageAttachments: options.resumeGatewayTurn ? options.resumeImageAttachments : undefined,
        documents,
        selectedSkills,
        activeReportId: turnActiveReportId,
        executionMode,
        turnId,
        runId,
        runAction,
        expectedSequence,
        websitePreviewContext,
        websitePreviewContextRef,
        canvasContext,
        canvasContextRef,
      };
      beginTurn(
        turnId,
        turnSessionId,
        requestController,
        optimisticMessage,
        baselineMessageCount,
        isLocalSession,
        activeReportSelectionBaseline
      );
      if (isLocalSession) {
        setMessages(prev => [...prev, optimisticMessage]);
        setIsLoading(true);
      }
      if (!options.resumeGatewayTurn && websitePreviewContext?.selection && websitePreviewContextRef) {
        void clearWebsitePreviewSelection?.(websitePreviewContextRef.contextVersion);
      }
      if (!options.resumeGatewayTurn && canvasContext?.selection && canvasContextRef) {
        clearCanvasSelection?.(canvasContextRef.contextVersion);
      }
      clearTurnLiveState(turnId, turnSessionId);

      const titleGenerationStarted = !options.resumeGatewayTurn && baselineMessageCount === 0 && Boolean(userMsg);
      if (titleGenerationStarted) {
        // Do not await: title summarization and the answer start independently.
        void apiCall({ action: 'auto_name_session', sessionId: turnSessionId, message: userMsg })
          .then(async response => {
            if (!response.ok) return;
            const data = await response.json();
            if (typeof data.name !== 'string' || !data.name.trim()) return;
            if (mountedRef.current && activeSessionIdRef.current === turnSessionId) {
              setSessionName(current => current || data.name);
            }
          })
          .catch(error => console.warn('[Research] Early title generation failed', error));
      }

      let streamStarted = false;
      let failedPersistedUserMessageId: string | undefined;
      let failedPersistedModelMessageId: string | undefined;
      let turnPersistenceConfirmed = false;
      try {
        // Include attachment metadata in the API call if present.
        const originalRequest = {
            action: options.resumeGatewayTurn ? 'gateway_resume' : 'send_message',
            previewCaptureClient: true,
            titleGenerationStarted,
            sessionId: turnSessionId,
            message: userMsg,
            modelId: turnModelId,
            executionMode,
            turnId,
            ...(runId ? { runId } : {}),
            ...(runAction ? { runAction } : {}),
            ...(expectedSequence !== undefined ? { expectedSequence } : {}),
            ...(turnReasoningEffort ? { reasoningEffort: turnReasoningEffort } : {}),
            ...(images && images.length > 0 ? { images } : {}),
            ...(documents && documents.length > 0 ? { documents } : {}),
            ...(selectedSkills !== undefined ? { selectedSkills } : {}),
            ...(turnActiveReportId ? { activeReportId: turnActiveReportId } : {}),
            ...(websitePreviewContext ? { websitePreviewContext } : {}),
            ...(websitePreviewContextRef ? { websitePreviewContextRef } : {}),
            ...(canvasContext ? { canvasContext } : {}),
            ...(canvasContextRef ? { canvasContextRef } : {}),
            ...(canvasEditingActive && isLocalSession ? { canvasEditingActive: true } : {}),
            ...(designSystemEditingActive ? { designSystemEditingActive: true } : {}),
          };
        const res = await apiCall(originalRequest, { signal: requestController.signal });
        const contentType = res.headers.get('content-type') || '';

        if (!res.ok) {
          if (contentType.includes('application/json')) {
            const err = await res.json();
            if (res.status === 409 && (runId || err.runStatus)) {
              const latest = err.runStatus as ResearchRunStatus | undefined;
              if (latest) {
                const next = setRuntimeRunStatus(turnSessionId, latest);
                if (isForegroundTurn(turnId, turnSessionId)) setRunStatus(next);
              }
              if (isForegroundTurn(turnId, turnSessionId)) {
                setMessages(previous =>
                  previous.filter(message => message.id !== tempUserMessageId)
                );
                clearTurnLiveState(turnId, turnSessionId);
              }
              toast.error(
                err.error?.message ||
                  'This run changed in another request. Showing its latest state.'
              );
              return false;
            }
            throw new Error(err.error?.message || 'Request failed');
          } else {
            const text = await res.text();
            console.error('[sendMessage] Non-JSON error response:', text.substring(0, 500));
            throw new Error(`Server error (${res.status}). Please try again.`);
          }
        }

        if (!contentType.includes('text/event-stream')) {
          const text = await res.text();
          console.error(
            '[sendMessage] Unexpected content type:',
            contentType,
            text.substring(0, 500)
          );
          throw new Error('Invalid response from server. Please try again.');
        }

        streamStarted = true;
        options.onAccepted?.();
        const {
          fullText,
          toolUsage,
          sources,
          thinkingText: streamThinking,
          traceTimeline,
          persistedTurn,
          contextUsage: streamContextUsage,
          runStatus: streamRunStatus,
          aborted,
          streamError,
        } = await handleStream(res, turnId, turnSessionId, originalRequest);
        const persistedSteerMessages = hydratePersistedSteerMessages(persistedTurn?.steerMessages);
        failedPersistedUserMessageId = persistedTurn?.userMessageId;
        failedPersistedModelMessageId = persistedTurn?.modelMessageId;
        turnPersistenceConfirmed = Boolean(persistedTurn?.modelMessageId);
        reconcilePersistedSteerMessages(turnId, turnSessionId, persistedSteerMessages);

        if (aborted || stoppedTurnIdsRef.current.has(turnId)) {
          // Reconcile the captured stopped trace after the server confirms its fence.
          cleanupStoppedTurnUi(turnId, turnSessionId, tempUserMessageId);
          return true;
        }

        if (streamError) {
          const serverStreamError = new Error(streamError);
          serverStreamError.name = SERVER_STREAM_ERROR_NAME;
          throw serverStreamError;
        }

        const hasMeaningfulOutput =
          fullText.trim().length > 0 ||
          streamThinking.trim().length > 0 ||
          toolUsage.length > 0 ||
          traceTimeline.length > 0 ||
          Boolean(turnSnapshotsRef.current.get(turnId)?.subagents.length) ||
          Boolean(streamRunStatus) ||
          Boolean(persistedTurn?.runStatus) ||
          Boolean(persistedTurn?.runReceipt);
        if (!hasMeaningfulOutput) {
          setRuntimeMessageCount(
            turnSessionId,
            getEmptyTurnMessageCount(
              baselineMessageCount,
              persistedTurn?.userMessageId,
              isLocalSession
            )
          );
          // If message generated no actual output, hide optimistic user row to avoid orphan state.
          if (isForegroundTurn(turnId, turnSessionId)) {
            setMessages(prev => prev.filter(msg => msg.id !== tempUserMessageId));
            clearTurnLiveState(turnId, turnSessionId);
          }
          return true;
        }

        const now = new Date().toISOString();
        const persistedUserMessageId = persistedTurn?.userMessageId || tempUserMessageId;
        const persistedModelMessageId = persistedTurn?.modelMessageId || `msg-${Date.now()}`;
        const finalTurnModelId = appliedModelIdByTurnRef.current.get(turnId) || turnModelId;
        const restoredUserMessage: Message = {
          id: persistedUserMessageId,
          role: 'user',
          content: userMsg,
          timestamp: userMessageTimestamp,
          targetModelId: turnModelId,
          reasoningEffort: turnReasoningEffort || null,
          images,
          imageAttachments: options.resumeGatewayTurn ? options.resumeImageAttachments : undefined,
          documents,
          selectedSkills,
          activeReportId: turnActiveReportId,
          executionMode,
          turnId,
          runId,
          runAction,
          expectedSequence,
          websitePreviewContext,
          websitePreviewContextRef,
          canvasContext,
          canvasContextRef,
        };
        const modelMessage: Message = {
          id: persistedModelMessageId,
          role: 'model',
          content: fullText,
          timestamp: now,
          modelId: finalTurnModelId,
          toolUsage,
          sources,
          traceTimeline: traceTimeline.length > 0 ? traceTimeline : undefined,
          subagents: turnSnapshotsRef.current.get(turnId)?.subagents,
          thinkingContent: streamThinking || undefined,
          skillUsage: persistedTurn?.skillUsage,
          executionMode: persistedTurn?.executionMode || executionMode,
          turnId,
          runStatus: persistedTurn?.runStatus || streamRunStatus || undefined,
          runReceipt: persistedTurn?.runReceipt,
        };
        const completedPendingResult: PendingTurnResult = {
          type: 'completed',
          optimisticUserMessageId: tempUserMessageId,
          userMessage: restoredUserMessage,
          steerMessages: persistedSteerMessages,
          modelMessage,
          baselineMessageCount,
          updatedAt: now,
          contextUsage: streamContextUsage,
          sessionName: persistedTurn?.sessionName,
          metadataApplied: false,
        };
        setRuntimeMessageCount(
          turnSessionId,
          baselineMessageCount + 2 + completedPendingResult.steerMessages.length
        );
        if (isForegroundTurn(turnId, turnSessionId)) {
          ensureTurnMessages(
            turnSessionId,
            tempUserMessageId,
            restoredUserMessage,
            [...completedPendingResult.steerMessages, modelMessage],
            baselineMessageCount
          );
          setSessionUpdatedAt(now);
          if (streamContextUsage !== undefined) {
            setContextUsage(streamContextUsage || null);
          }
          if (persistedTurn?.sessionName !== undefined) {
            setSessionName(persistedTurn.sessionName || null);
          }
          appendRuntimePendingTurnResult(turnSessionId, {
            ...completedPendingResult,
            metadataApplied: true,
          });
          clearTurnLiveState(turnId, turnSessionId);
        } else {
          appendRuntimePendingTurnResult(turnSessionId, completedPendingResult);
          markSessionUnread(turnSessionId);
        }
        return true;
      } catch (err) {
        if (stoppedTurnIdsRef.current.has(turnId)) {
          cleanupStoppedTurnUi(turnId, turnSessionId, tempUserMessageId);
          return streamStarted;
        }
        const shouldRecover =
          streamStarted && !(err instanceof Error && err.name === SERVER_STREAM_ERROR_NAME);
        const recovered = shouldRecover
          ? await recoverPersistedTurn(turnSessionId, turnId, baselineMessageCount)
          : null;
        if (recovered) {
          turnPersistenceConfirmed = true;
          const hydratedRecovered = {
            ...recovered,
            messages: hydratePersistedSteerMessages(recovered.messages),
          };
          reconcilePersistedSteerMessages(turnId, turnSessionId, hydratedRecovered.messages);
          applyRecoveredTurn(turnSessionId, turnId, hydratedRecovered);
          return true;
        }
        const errorText = err instanceof Error ? err.message : 'Failed';
        if (
          !streamStarted &&
          (suppressPreStreamFailureTurn || (restoreDocumentsOnReject && documents?.length))
        ) {
          setRuntimeMessageCount(turnSessionId, baselineMessageCount);
          if (isForegroundTurn(turnId, turnSessionId)) {
            setMessages(previous => previous.filter(message => message.id !== tempUserMessageId));
            toast.error(errorText);
            clearTurnLiveState(turnId, turnSessionId);
          }
          return false;
        }
        const now = new Date().toISOString();
        const errorMessage: Message = {
          id: `error-${turnId}`,
          role: 'model',
          content: `Response failed: ${errorText}`,
          timestamp: now,
          modelId: turnModelId,
          turnId,
          subagents: cancelActiveSubagentStatuses(turnSnapshotsRef.current.get(turnId)?.subagents || []),
        };
        const failedPendingResult: PendingTurnResult = {
          type: 'failed',
          optimisticUserMessageId: tempUserMessageId,
          userMessage: optimisticMessage,
          errorMessage,
          baselineMessageCount,
          persistedUserMessageId: failedPersistedUserMessageId,
          persistedModelMessageId: failedPersistedModelMessageId,
          errorText,
          updatedAt: now,
          notified: false,
          metadataApplied: false,
        };
        setRuntimeMessageCount(
          turnSessionId,
          getFailedTurnMessageCount(
            baselineMessageCount,
            failedPersistedUserMessageId,
            failedPersistedModelMessageId,
            isLocalSession
          )
        );
        if (isForegroundTurn(turnId, turnSessionId)) {
          ensureTurnMessages(
            turnSessionId,
            tempUserMessageId,
            optimisticMessage,
            errorMessage,
            baselineMessageCount
          );
          toast.error(errorText);
          appendRuntimePendingTurnResult(turnSessionId, {
            ...failedPendingResult,
            notified: true,
            metadataApplied: true,
          });
          clearTurnLiveState(turnId, turnSessionId);
        } else {
          appendRuntimePendingTurnResult(turnSessionId, failedPendingResult);
          markSessionUnread(turnSessionId);
        }
        return streamStarted;
      } finally {
        const cancellation = researchChatRuntimeStore.turnCancellationPromises.get(turnId);
        let cancellationResult: TurnCancellationResult = null;
        if (cancellation) {
          cancellationResult = await cancellation.catch(() => null);
          turnPersistenceConfirmed ||= cancellationResult === 'completed';
        }
        if (cancellationResult === 'completed') {
          const recovered = await recoverPersistedTurn(
            turnSessionId,
            turnId,
            baselineMessageCount,
            {
              allowStoppedTurn: true,
              timeoutMs: 30_000,
              pollMs: 500,
            }
          );
          if (recovered) {
            const hydratedRecovered = {
              ...recovered,
              messages: hydratePersistedSteerMessages(recovered.messages),
            };
            reconcilePersistedSteerMessages(turnId, turnSessionId, hydratedRecovered.messages);
            applyRecoveredTurn(turnSessionId, turnId, hydratedRecovered, {
              restoreCurrentSession: true,
            });
          }
        }
        if (cancellationResult === 'cancelled' || cancellationResult === 'failed') {
          const stoppedTurn = inFlightTurnsRef.current.get(turnId);
          const stoppedSnapshot = stoppedTurn?.stoppedSnapshot;
          if (stoppedTurn && stoppedSnapshot?.subagents.length) {
            const now = new Date().toISOString();
            const modelMessage: Message = {
              id: `stopped-${turnId}`, role: 'model', timestamp: now, turnId,
              content: stoppedSnapshot.streamingText,
              modelId: appliedModelIdByTurnRef.current.get(turnId) || turnModelId,
              subagents: cancelActiveSubagentStatuses(stoppedSnapshot.subagents),
              toolUsage: stoppedSnapshot.activeTools.map(finalizePendingToolStatus),
              traceTimeline: stoppedSnapshot.streamTimeline.map(item =>
                item.type === 'tool' ? finalizePendingToolStatus(item) : item),
            };
            const visible = mountedRef.current && activeSessionIdRef.current === turnSessionId;
            appendRuntimePendingTurnResult(turnSessionId, {
              type: 'completed', optimisticUserMessageId: tempUserMessageId,
              userMessage: stoppedTurn.optimisticMessage, steerMessages: [], modelMessage,
              baselineMessageCount, updatedAt: now, metadataApplied: visible,
            });
            setRuntimeMessageCount(turnSessionId, baselineMessageCount + 2);
            if (visible) {
              ensureTurnMessages(turnSessionId, tempUserMessageId, stoppedTurn.optimisticMessage,
                modelMessage, baselineMessageCount);
              setSessionUpdatedAt(now);
            } else {
              markSessionUnread(turnSessionId);
            }
          }
        }
        turnPersistenceConfirmed ||=
          researchChatRuntimeStore.persistenceConfirmedTurnIds.has(turnId);
        if (turnPersistenceConfirmed) {
          researchChatRuntimeStore.persistenceConfirmedTurnIds.add(turnId);
          setTimeout(() => {
            researchChatRuntimeStore.persistenceConfirmedTurnIds.delete(turnId);
            researchChatRuntimeStore.persistedSteerIdsByTurn.delete(turnId);
          }, 60_000);
        }
        const retainedTurnContext = inFlightTurnsRef.current.get(turnId)?.optimisticMessage;
        const pendingSteerIds = pendingSteerIdsByTurnRef.current.get(turnId);
        if (pendingSteerIds?.size) {
          pendingSteerIds.forEach(pendingSteerId => {
            toast.dismiss(getSteerToastId(pendingSteerId));
          });
        }
        pendingSteerIdsByTurnRef.current.delete(turnId);
        for (const [steerId, receiptState] of steerReceiptStateByIdRef.current) {
          if (receiptState.turnId === turnId) {
            const retainedOptions = pendingSteerOptionsByIdRef.current.get(steerId);
            if (
              !isSteerTranscriptPersisted(turnId, steerId) &&
              retainedOptions &&
              (receiptState.postAccepted || receiptState.applied)
            ) {
              retainSteerInQueue(turnSessionId, steerId, retainedOptions, retainedTurnContext);
            }
            steerReceiptStateByIdRef.current.delete(steerId);
            pendingSteerOptionsByIdRef.current.delete(steerId);
          }
        }
        researchChatRuntimeStore.turnCancellationPromises.delete(turnId);
        appliedModelIdByTurnRef.current.delete(turnId);
        latestSteerContextSequenceByTurnRef.current.delete(turnId);
        const shouldClearLoading = isForegroundTurn(turnId, turnSessionId);
        finishTurn(turnId, turnSessionId);
        if (shouldClearLoading) {
          setIsLoading(false);
        }
        void drainQueuedSessionRef.current(turnSessionId);
      }
    },
    [
      sessionId,
      messages.length,
      applyRecoveredTurn,
      apiCall,
      selectedModelId,
      reasoningEffortModelIds,
      getActiveReportId,
      getWebsitePreviewContextSnapshot,
      getWebsitePreviewContextReference,
      clearWebsitePreviewSelection,
      getCanvasContextSnapshot,
      getCanvasContextReference,
      clearCanvasSelection,
      canvasEditingActive,
      designSystemEditingActive,
      beginTurn,
      clearTurnLiveState,
      cleanupStoppedTurnUi,
      ensureTurnMessages,
      finishTurn,
      handleStream,
      hydratePersistedSteerMessages,
      isForegroundTurn,
      markSessionUnread,
      reconcilePersistedSteerMessages,
      recoverPersistedTurn,
      retainSteerInQueue,
      setMessages,
      setSessionUpdatedAt,
      setSessionName,
    ]
  );

  useEffect(() => {
    if (!sessionId || !runtimeOwnerMatches || getRuntimeInFlightTurnId(sessionId)) return;
    let current = true;
    void (async () => {
      try {
        const response = await apiCall({ action: 'gateway_active_turn', sessionId });
        if (!response.ok) return;
        const data = await response.json();
        if (!current || !data.turn?.turnId || getRuntimeInFlightTurnId(sessionId)) return;
        const input = data.input || {};
        await sendMessageInternal({
          resumeGatewayTurn: true,
          turnId: data.turn.turnId,
          targetSessionId: sessionId,
          content: typeof input.message === 'string' ? input.message : '(Continuing accepted work)',
          resumeImageAttachments: input.imageAttachments,
          websitePreviewContext: input.websitePreviewContext,
          websitePreviewContextRef: input.websitePreviewContextRef,
          canvasContext: input.canvasContext,
          canvasContextRef: input.canvasContextRef,
          reasoningEffort: input.reasoningEffort,
          targetModelId: input.modelId,
          selectedSkills: input.selectedSkills,
          targetActiveReportId: input.activeReportId,
          documents: input.documents,
          executionMode: input.executionMode,
          runId: input.runId,
          runAction: input.runAction,
          expectedSequence: input.expectedSequence,
        });
      } catch {
        // Loading history remains available when the gateway is temporarily unreachable.
      }
    })();
    return () => { current = false; };
  }, [apiCall, runtimeOwnerMatches, sendMessageInternal, sessionId]);

  // Send message from input field
  const sendMessage = useCallback(async () => {
    if (!inputMessage.trim()) return;
    const content = inputMessage.trim();
    setInputMessage('');
    if (sessionId && isRuntimeSessionLoading(sessionId)) {
      enqueueMessage(content);
      return;
    }
    await sendMessageInternal({ content });
  }, [inputMessage, sessionId, enqueueMessage, sendMessageInternal]);

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  const sendMessageDirect = useCallback(
    async (
      content: string,
      images?: ImageAttachment[],
      selectedSkills?: SelectedAgentSkill[],
      executionMode: ResearchExecutionMode = 'standard',
      documents?: DocumentAttachment[],
      restoreDocumentsOnReject = false,
      context?: DirectMessageContext
    ): Promise<boolean> => {
      if (sessionId && isRuntimeSessionLoading(sessionId)) {
        enqueueMessage(content, images, selectedSkills, executionMode, documents, context);
        return true;
      }
      return sendMessageInternal({
        content,
        images,
        documents,
        selectedSkills,
        onAccepted: context?.onAccepted,
        targetModelId: context?.targetModelId,
        ...(context && Object.prototype.hasOwnProperty.call(context, 'targetActiveReportId')
          ? { targetActiveReportId: context?.targetActiveReportId }
          : {}),
        executionMode,
        reasoningEffort: context?.reasoningEffort,
        restoreDocumentsOnReject,
      });
    },
    [sessionId, enqueueMessage, sendMessageInternal]
  );

  const upsertAcceptedSteerMessage = useCallback(
    (
      steerSessionId: string,
      turnId: string,
      steerId: string,
      options: SteerMessageOptions,
      sequence?: number
    ) => {
      if (activeSessionIdRef.current !== steerSessionId) return;
      const optimisticSteer: Message = {
        id: `temp-steer-${steerId}`,
        role: 'user',
        content: getSteerMessageContent(options),
        timestamp: new Date().toISOString(),
        targetModelId: options.targetModelId,
        reasoningEffort: options.reasoningEffort,
        images: options.images,
        documents: options.documents,
        selectedSkills: options.selectedSkills,
        activeReportId: options.targetActiveReportId,
        executionMode: options.executionMode,
        websitePreviewContext: options.websitePreviewContext,
        websitePreviewContextRef: options.websitePreviewContextRef,
        canvasContext: options.canvasContext,
        canvasContextRef: options.canvasContextRef,
        turnId,
        inputKind: 'steer',
        steerId,
        ...(sequence !== undefined ? { steerSequence: sequence } : {}),
      };

      setMessages(current => {
        if (
          current.some(
            message => message.inputKind === 'steer' && message.steerId === optimisticSteer.steerId
          )
        ) {
          return current;
        }

        const insertBeforeIndex = current.findIndex(message => {
          if (message.turnId !== turnId) return false;
          if (message.role === 'model') return true;
          return (
            optimisticSteer.steerSequence !== undefined &&
            message.inputKind === 'steer' &&
            message.steerSequence !== undefined &&
            message.steerSequence > optimisticSteer.steerSequence
          );
        });
        if (insertBeforeIndex < 0) return [...current, optimisticSteer];
        return [
          ...current.slice(0, insertBeforeIndex),
          optimisticSteer,
          ...current.slice(insertBeforeIndex),
        ];
      });
    },
    [setMessages]
  );

  const steerMessageInternal = useCallback(
    async (options: SteerMessageOptions): Promise<SteerSubmissionResult> => {
      const steerSessionId = options.targetSessionId || sessionId;
      // A retry remains bound to its original turn even if a newer turn starts.
      let expectedTurnId = options.targetTurnId || steerReceiptStateByIdRef.current.get(options.steerId)?.turnId || getRuntimeInFlightTurnId(steerSessionId);
      if (!steerSessionId) return { accepted: false, outcome: 'definitive_rejection', turnId: expectedTurnId || undefined, steerId: options.steerId };
      if (!expectedTurnId) {
        try {
          const active = await apiCall({ action: 'gateway_active_turn', sessionId: steerSessionId });
          if (!active.ok) throw new Error('Status unavailable');
          expectedTurnId = (await active.json()).turn?.turnId;
          if (!expectedTurnId) return { accepted: false, outcome: 'definitive_rejection', turnId: expectedTurnId || undefined, steerId: options.steerId };
        } catch {
          return { accepted: false, outcome: 'ambiguous', turnId: expectedTurnId || undefined, steerId: options.steerId };
        }
      }
      const canonicalTerminal = async () => {
        try {
          const status = await apiCall({ action: 'gateway_turn_status', sessionId: steerSessionId, turnId: expectedTurnId });
          return status.ok && ['completed', 'cancelled', 'failed', 'waiting_for_approval'].includes((await status.json()).phase);
        } catch { return false; }
      };

      const targetModelId = options.targetModelId;
      const hasReasoningEffort = Object.prototype.hasOwnProperty.call(options, 'reasoningEffort');
      const reasoningEffort = hasReasoningEffort
        ? options.reasoningEffort
        : targetModelId && reasoningEffortModelIds?.includes(targetModelId)
          ? getStoredResearchReasoningEffort()
          : undefined;
      const normalizedOptions: SteerMessageOptions = {
        ...options,
        ...(targetModelId ? { targetModelId } : {}),
        ...(hasReasoningEffort || reasoningEffort !== undefined ? { reasoningEffort } : {}),
      };
      pendingSteerOptionsByIdRef.current.set(options.steerId, normalizedOptions);
      const existingReceipt = steerReceiptStateByIdRef.current.get(options.steerId);
      steerReceiptStateByIdRef.current.set(options.steerId, {
        turnId: expectedTurnId,
        applied: existingReceipt?.applied || false,
        postAccepted: existingReceipt?.postAccepted || false,
        ...(existingReceipt?.sequence !== undefined ? { sequence: existingReceipt.sequence } : {}),
      });
      const activeTurn = inFlightTurnsRef.current.get(expectedTurnId);
      if (!activeTurn?.steeringReady) {
        toast.loading('Steer queued — waiting for the response to start.', {
          id: getSteerToastId(options.steerId),
        });
        const steeringReady = await waitForRuntimeSteeringWindow(steerSessionId, expectedTurnId);
        if (!steeringReady) {
          // Local stream state can lag the gateway. Let the idempotent control
          // endpoint reconcile admission rather than announcing an ended turn.
          toast.dismiss(getSteerToastId(options.steerId));
        }
      }
      const activeRunStatus = getRuntimeRunStatus(steerSessionId);

      const requestBody = {
        action: 'steer_turn',
        sessionId: steerSessionId,
        expectedTurnId,
        steerId: options.steerId,
        ...(options.content.trim() ? { message: options.content.trim() } : {}),
        ...(options.images?.length ? { images: options.images } : {}),
        ...(options.documents?.length ? { documents: options.documents } : {}),
        ...(options.selectedSkills !== undefined ? { selectedSkills: options.selectedSkills } : {}),
        ...(targetModelId ? { targetModelId } : {}),
        ...(reasoningEffort !== undefined ? { reasoningEffort } : {}),
        ...(options.executionMode ? { executionMode: options.executionMode } : {}),
        ...(Object.prototype.hasOwnProperty.call(options, 'targetActiveReportId')
          ? { activeReportId: options.targetActiveReportId }
          : {}),
        ...(activeRunStatus?.runId ? { runId: activeRunStatus.runId } : {}),
        ...(options.websitePreviewContext
          ? { websitePreviewContext: options.websitePreviewContext }
          : {}),
        ...(options.websitePreviewContextRef
          ? { websitePreviewContextRef: options.websitePreviewContextRef }
          : {}),
        ...(options.canvasContext ? { canvasContext: options.canvasContext } : {}),
        ...(options.canvasContextRef ? { canvasContextRef: options.canvasContextRef } : {}),
      };
      let response: Response | null = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const candidate = await apiCall(requestBody);
          if (attempt === 0 && [502, 503, 504].includes(candidate.status)) continue;
          response = candidate;
          break;
        } catch {
          if (attempt === 1) break;
        }
      }
      if (!response) {
        const receiptState = steerReceiptStateByIdRef.current.get(options.steerId);
        if (receiptState?.applied) {
          upsertAcceptedSteerMessage(
            steerSessionId,
            expectedTurnId,
            options.steerId,
            normalizedOptions,
            receiptState.sequence
          );
          toast.success('Steer applied.', { id: getSteerToastId(options.steerId) });
          return { accepted: true, outcome: 'accepted', turnId: expectedTurnId || undefined, steerId: options.steerId };
        }
        toast.error('Unable to submit the steer. Please try again.');
        return { accepted: false, outcome: 'ambiguous', turnId: expectedTurnId || undefined, steerId: options.steerId };
      }

      let data: SteerTurnResponse = {};
      if ((response.headers.get('content-type') || '').includes('application/json')) {
        try {
          data = (await response.json()) as SteerTurnResponse;
        } catch {
          // Fall through to the status-specific error below.
        }
      }

      let verifiedTerminalConflict = false;
      if (response.status === 409 && await canonicalTerminal()) {
        // Completion fences new acceptance. Re-read the idempotent receipt only
        // after that fence, so a lost accepted response cannot become a duplicate request.
        try {
          response = await apiCall(requestBody);
          data = (await response.json()) as SteerTurnResponse;
          verifiedTerminalConflict = response.status === 409;
        } catch {
          return { accepted: false, outcome: 'ambiguous', turnId: expectedTurnId || undefined, steerId: options.steerId };
        }
      }

      const acceptedSteerId = data.steerId || options.steerId;
      const receiptState = steerReceiptStateByIdRef.current.get(acceptedSteerId);
      if (!response.ok && !data.duplicate && !receiptState?.applied) {
        const ambiguous = response.status >= 500 || data.error?.code === 'STEER_ACCEPTANCE_UNKNOWN' ||
          (response.status === 409 && !verifiedTerminalConflict);
        toast.error(ambiguous ? 'Your update is still being checked. Retry to check the same update.' :
          'This update wasn’t accepted. You can send it as a new request.', { id: getSteerToastId(options.steerId) });
        if (!ambiguous) {
          pendingSteerOptionsByIdRef.current.delete(options.steerId);
          steerReceiptStateByIdRef.current.delete(options.steerId);
        }
        return {
          accepted: false,
          outcome: ambiguous ? 'ambiguous' : 'definitive_rejection',
          turnId: expectedTurnId || undefined, steerId: options.steerId,
        };
      }
      if (data.accepted === false && !data.duplicate && !receiptState?.applied) {
        toast.error('This update wasn’t accepted. You can send it as a new request.', { id: getSteerToastId(options.steerId) });
        pendingSteerOptionsByIdRef.current.delete(options.steerId);
        steerReceiptStateByIdRef.current.delete(options.steerId);
        return {
          accepted: false,
          outcome: 'definitive_rejection',
          turnId: expectedTurnId || undefined, steerId: options.steerId,
        };
      }

      const responseContext = parseSteerContextSnapshot(data.context);
      const responseContextSequence =
        typeof data.contextSequence === 'number'
          ? data.contextSequence
          : typeof data.sequence === 'number'
            ? data.sequence
            : undefined;
      if (responseContext && responseContextSequence !== undefined) {
        applyResolvedSteerContext(expectedTurnId, responseContext, responseContextSequence);
      } else {
        applyAcceptedSteerContext(expectedTurnId, normalizedOptions, data.sequence);
      }
      if (acceptedSteerId !== options.steerId) {
        pendingSteerOptionsByIdRef.current.delete(options.steerId);
        steerReceiptStateByIdRef.current.delete(options.steerId);
      }
      pendingSteerOptionsByIdRef.current.set(acceptedSteerId, normalizedOptions);
      const turnPersistenceConfirmed =
        researchChatRuntimeStore.persistenceConfirmedTurnIds.has(expectedTurnId) ||
        (['completed', 'cancelled', 'failed'].includes(data.turnStatus || '') && await canonicalTerminal());
      let retainedForNextTurn = false;
      if (isSteerTranscriptPersisted(expectedTurnId, acceptedSteerId)) {
        pendingSteerIdsByTurnRef.current.get(expectedTurnId)?.delete(acceptedSteerId);
        pendingSteerOptionsByIdRef.current.delete(acceptedSteerId);
        steerReceiptStateByIdRef.current.delete(acceptedSteerId);
        toast.success('Steer applied.', { id: getSteerToastId(acceptedSteerId) });
      } else if (turnPersistenceConfirmed && data.status !== 'applied' && !receiptState?.applied) {
        retainSteerInQueue(
          steerSessionId,
          acceptedSteerId,
          normalizedOptions,
          activeTurn?.optimisticMessage
        );
        retainedForNextTurn = true;
        pendingSteerOptionsByIdRef.current.delete(acceptedSteerId);
        steerReceiptStateByIdRef.current.delete(acceptedSteerId);
      } else if (data.status === 'applied' || receiptState?.applied) {
        pendingSteerIdsByTurnRef.current.get(expectedTurnId)?.delete(acceptedSteerId);
        steerReceiptStateByIdRef.current.set(acceptedSteerId, {
          turnId: expectedTurnId,
          applied: true,
          postAccepted: true,
          ...(typeof data.sequence === 'number' ? { sequence: data.sequence } : {}),
        });
        toast.success('Steer applied.', { id: getSteerToastId(acceptedSteerId) });
      } else {
        steerReceiptStateByIdRef.current.set(acceptedSteerId, {
          turnId: expectedTurnId,
          applied: false,
          postAccepted: true,
          ...(typeof data.sequence === 'number' ? { sequence: data.sequence } : {}),
        });
        const pendingForTurn =
          pendingSteerIdsByTurnRef.current.get(expectedTurnId) || new Set<string>();
        pendingForTurn.add(acceptedSteerId);
        pendingSteerIdsByTurnRef.current.set(expectedTurnId, pendingForTurn);
        toast.loading(
          data.duplicate
            ? 'Steer already queued — waiting for the current step.'
            : 'Steer queued — waiting for the current step.',
          { id: getSteerToastId(acceptedSteerId) }
        );
      }
      if (!retainedForNextTurn) {
        upsertAcceptedSteerMessage(
          steerSessionId,
          expectedTurnId,
          acceptedSteerId,
          normalizedOptions,
          data.sequence ?? receiptState?.sequence
        );
      }
      if (normalizedOptions.websitePreviewContextRef) {
        void clearWebsitePreviewSelection?.(
          normalizedOptions.websitePreviewContextRef.contextVersion
        );
      }
      if (normalizedOptions.canvasContextRef) {
        clearCanvasSelection?.(normalizedOptions.canvasContextRef.contextVersion);
      }
      return { accepted: true, outcome: 'accepted', turnId: expectedTurnId || undefined, steerId: acceptedSteerId };
    },
    [
      apiCall,
      applyAcceptedSteerContext,
      applyResolvedSteerContext,
      clearWebsitePreviewSelection,
      clearCanvasSelection,
      reasoningEffortModelIds,
      retainSteerInQueue,
      sessionId,
      upsertAcceptedSteerMessage,
    ]
  );

  const steerMessageDirect = useCallback(
    async (
      content: string,
      images?: ImageAttachment[],
      selectedSkills?: SelectedAgentSkill[],
      documents?: DocumentAttachment[],
      options: DirectSteerOptions = {}
    ): Promise<SteerSubmissionResult> => {
      const expectedTurnId = getRuntimeInFlightTurnId(sessionId);
      const activeTurn = expectedTurnId ? inFlightTurnsRef.current.get(expectedTurnId) : undefined;
      const currentActiveReportId = getActiveReportId?.() || null;
      const reportSelectionChanged = Boolean(
        activeTurn && currentActiveReportId !== activeTurn.activeReportSelectionBaseline
      );
      const directReasoningEffort =
        options.reasoningEffort ||
        (options.targetModelId && reasoningEffortModelIds?.includes(options.targetModelId)
          ? getStoredResearchReasoningEffort()
          : undefined);
      const websitePreviewContext = getWebsitePreviewContextSnapshot?.() || undefined;
      const websitePreviewContextRef = websitePreviewContext
        ? {
            previewId: websitePreviewContext.previewId,
            contextVersion: websitePreviewContext.contextVersion,
          }
        : getWebsitePreviewContextReference?.() || undefined;
      const canvasContext = getCanvasContextSnapshot?.() || undefined;
      const canvasContextRef = canvasContext
        ? {
            artifactId: canvasContext.artifactId,
            version: canvasContext.version,
            contextVersion: canvasContext.contextVersion,
          }
        : getCanvasContextReference?.() || undefined;
      const directOptions: Omit<SteerMessageOptions, 'steerId' | 'targetSessionId'> = {
        content,
        images,
        documents,
        selectedSkills,
        ...(options.targetModelId ? { targetModelId: options.targetModelId } : {}),
        ...(directReasoningEffort ? { reasoningEffort: directReasoningEffort } : {}),
        ...(options.executionMode ? { executionMode: options.executionMode } : {}),
        ...(reportSelectionChanged ? { targetActiveReportId: currentActiveReportId } : {}),
        ...(websitePreviewContext ? { websitePreviewContext } : {}),
        ...(websitePreviewContextRef ? { websitePreviewContextRef } : {}),
        ...(canvasContext ? { canvasContext } : {}),
        ...(canvasContextRef ? { canvasContextRef } : {}),
      };
      // Draft identity survives gateway discovery and subsequent local hydration.
      // The cached value, rather than the lookup key, owns the resolved turn.
      const signature = getDirectSteerSignature('', directOptions);
      const ambiguousSteers = sessionId
        ? researchChatRuntimeStore.ambiguousDirectSteerBySession.get(sessionId)
        : undefined;
      const cachedSteer = ambiguousSteers?.get(signature);
      const steerId = cachedSteer?.steerId || crypto.randomUUID();
      const result = await steerMessageInternal({
        ...directOptions,
        ...(cachedSteer?.turnId ? { targetTurnId: cachedSteer.turnId } : {}),
        steerId,
      });
      if (sessionId) {
        if (result.outcome === 'ambiguous') {
          const nextAmbiguousSteers = ambiguousSteers || new Map();
          nextAmbiguousSteers.set(signature, {
            turnId: result.turnId || steerReceiptStateByIdRef.current.get(steerId)?.turnId || expectedTurnId || '',
            steerId,
          });
          researchChatRuntimeStore.ambiguousDirectSteerBySession.set(
            sessionId,
            nextAmbiguousSteers
          );
        } else {
          const currentSteer = ambiguousSteers?.get(signature);
          if (currentSteer?.steerId === steerId) {
            ambiguousSteers?.delete(signature);
          }
          if (ambiguousSteers?.size === 0) {
            researchChatRuntimeStore.ambiguousDirectSteerBySession.delete(sessionId);
          }
        }
      }
      return result;
    },
    [
      getActiveReportId,
      getWebsitePreviewContextSnapshot,
      getWebsitePreviewContextReference,
      getCanvasContextSnapshot,
      getCanvasContextReference,
      reasoningEffortModelIds,
      sessionId,
      steerMessageInternal,
    ]
  );

  const steerScheduledCommand = useCallback(
    async (scheduledCommandId: string) => {
      if (!sessionId) return;
      const queuedCommands = getRuntimeScheduledCommands(sessionId);
      const queuedIndex = queuedCommands.findIndex(command => command.id === scheduledCommandId);
      if (queuedIndex < 0) return;

      const queuedCommand = queuedCommands[queuedIndex] as RichScheduledCommand;
      const wasDrainBlocked =
        researchChatRuntimeStore.dequeueBlockedCommandIds.delete(scheduledCommandId);
      setRuntimeScheduledCommands(
        sessionId,
        queuedCommands.filter(command => command.id !== scheduledCommandId)
      );
      const result = await steerMessageInternal({
        content: queuedCommand.message || '',
        images: queuedCommand.images,
        documents: queuedCommand.documents,
        selectedSkills: queuedCommand.selectedSkills || [],
        targetSessionId: sessionId,
        ...(queuedCommand.modelId ? { targetModelId: queuedCommand.modelId } : {}),
        ...(queuedCommand.activeReportId !== undefined
          ? { targetActiveReportId: queuedCommand.activeReportId }
          : {}),
        ...(queuedCommand.executionMode ? { executionMode: queuedCommand.executionMode } : {}),
        ...(queuedCommand.reasoningEffort
          ? { reasoningEffort: queuedCommand.reasoningEffort }
          : {}),
        ...(queuedCommand.websitePreviewContext
          ? { websitePreviewContext: queuedCommand.websitePreviewContext }
          : {}),
        ...(queuedCommand.websitePreviewContextRef
          ? { websitePreviewContextRef: queuedCommand.websitePreviewContextRef }
          : {}),
        ...(queuedCommand.canvasContext ? { canvasContext: queuedCommand.canvasContext } : {}),
        ...(queuedCommand.canvasContextRef
          ? { canvasContextRef: queuedCommand.canvasContextRef }
          : {}),
        steerId: getScheduledCommandSteerId(queuedCommand),
      });
      if (result.accepted) return;
      if (steerReceiptStateByIdRef.current.get(result.steerId)?.applied) return;

      if (wasDrainBlocked) {
        researchChatRuntimeStore.dequeueBlockedCommandIds.add(scheduledCommandId);
      }
      setRuntimeScheduledCommands(sessionId, current => {
        if (current.some(command => command.id === queuedCommand.id)) return current;
        const restored = [...current];
        restored.splice(Math.min(queuedIndex, restored.length), 0, queuedCommand);
        return restored;
      });
      toast.info('The item was kept in the queue.');
    },
    [sessionId, steerMessageInternal]
  );

  const drainQueuedSession = useCallback(
    async (queuedSessionId: string) => {
      if (
        isRuntimeSessionLoading(queuedSessionId) ||
        researchChatRuntimeStore.dequeueInFlightSessionIds.has(queuedSessionId)
      ) {
        return;
      }

      const queuedMessages = getRuntimeScheduledCommands(queuedSessionId);
      if (queuedMessages.length === 0) return;

      const [next, ...rest] = queuedMessages;
      if (researchChatRuntimeStore.dequeueBlockedCommandIds.has(next.id)) return;
      const richNext = next as RichScheduledCommand;
      if (!next.message && !next.images?.length && !next.documents?.length) {
        researchChatRuntimeStore.dequeueBlockedCommandIds.add(next.id);
        emitResearchChatRuntimeChange();
        return;
      }

      researchChatRuntimeStore.dequeueInFlightSessionIds.add(queuedSessionId);
      setRuntimeScheduledCommands(queuedSessionId, rest);
      let restoreQueuedCommand = false;
      try {
        if (next.message || next.images?.length || next.documents?.length) {
          const accepted = await sendMessageInternal({
            content: next.message || '',
            images: next.images,
            documents: next.documents,
            selectedSkills: next.selectedSkills,
            targetSessionId: queuedSessionId,
            targetModelId: next.modelId,
            targetActiveReportId: next.activeReportId,
            executionMode: next.executionMode || 'standard',
            turnId: `research-turn-${crypto.randomUUID()}`,
            reasoningEffort: richNext.reasoningEffort,
            websitePreviewContext: next.websitePreviewContext,
            websitePreviewContextRef: next.websitePreviewContextRef,
            canvasContext: next.canvasContext,
            canvasContextRef: next.canvasContextRef,
            restoreDocumentsOnReject: false,
            suppressPreStreamFailureTurn: true,
          });
          restoreQueuedCommand = !accepted;
        }
      } catch {
        restoreQueuedCommand = true;
        toast.error('The queued item could not be sent and remains in the queue.');
      } finally {
        if (restoreQueuedCommand) {
          researchChatRuntimeStore.dequeueBlockedCommandIds.add(next.id);
          setRuntimeScheduledCommands(queuedSessionId, current =>
            current.some(command => command.id === next.id) ? current : [next, ...current]
          );
        } else {
          researchChatRuntimeStore.dequeueBlockedCommandIds.delete(next.id);
        }
        researchChatRuntimeStore.dequeueInFlightSessionIds.delete(queuedSessionId);
        emitResearchChatRuntimeChange();
        if (!restoreQueuedCommand && getRuntimeScheduledCommands(queuedSessionId).length > 0) {
          queueMicrotask(() => {
            void drainQueuedSessionRef.current(queuedSessionId);
          });
        }
      }
    },
    [sendMessageInternal]
  );

  useEffect(() => {
    drainQueuedSessionRef.current = drainQueuedSession;
  }, [drainQueuedSession]);

  useEffect(() => {
    for (const queuedSessionId of researchChatRuntimeStore.scheduledCommandsBySession.keys()) {
      void drainQueuedSession(queuedSessionId);
    }
  }, [drainQueuedSession, runtimeRevision, scheduledCommands]);

  // Retry: replay the original input and every persisted steer in order.
  const retryMessage = useCallback(
    async (modelMessageId: string) => {
      if (!sessionId || isRuntimeSessionLoading(sessionId)) return;

      const modelIdx = messages.findIndex(m => m.id === modelMessageId);
      if (modelIdx < 1) return;

      const modelMessage = messages[modelIdx];
      const findOriginalUserMessageIndex = (requireMatchingTurn: boolean) => {
        for (let index = modelIdx - 1; index >= 0; index -= 1) {
          const candidate = messages[index];
          if (candidate.role === 'model') break;
          if (
            candidate.role === 'user' &&
            candidate.inputKind !== 'steer' &&
            (!requireMatchingTurn || candidate.turnId === modelMessage.turnId)
          ) {
            return index;
          }
        }
        return -1;
      };
      const matchingUserIndex = modelMessage.turnId ? findOriginalUserMessageIndex(true) : -1;
      const userMessageIndex =
        matchingUserIndex >= 0 ? matchingUserIndex : findOriginalUserMessageIndex(false);
      if (userMessageIndex < 0) return;
      const userMsg = messages[userMessageIndex];
      const turnSteers = messages
        .slice(userMessageIndex + 1, modelIdx)
        .filter(
          message =>
            message.role === 'user' &&
            message.inputKind === 'steer' &&
            (!modelMessage.turnId || message.turnId === modelMessage.turnId)
        )
        .sort(
          (left, right) =>
            (left.steerSequence || Number.MAX_SAFE_INTEGER) -
            (right.steerSequence || Number.MAX_SAFE_INTEGER)
        );
      const unavailableSteeringImages = turnSteers.some(
        steer => steer.imageAttachments?.length && !steer.images?.length
      );
      if (unavailableSteeringImages) {
        toast.error('Retry requires the original steering images. Reattach them and send again.');
        return;
      }

      const retryTurnId = `research-turn-${crypto.randomUUID()}`;
      const retryPromise = sendMessageInternal({
        content: userMsg.content,
        images: userMsg.images,
        documents: userMsg.documents,
        selectedSkills: userMsg.selectedSkills,
        targetModelId: userMsg.targetModelId || userMsg.modelId,
        ...(Object.prototype.hasOwnProperty.call(userMsg, 'activeReportId')
          ? { targetActiveReportId: userMsg.activeReportId }
          : {}),
        executionMode: userMsg.executionMode || 'standard',
        ...(userMsg.reasoningEffort ? { reasoningEffort: userMsg.reasoningEffort } : {}),
        turnId: retryTurnId,
        runId: userMsg.runId,
        runAction: userMsg.runAction,
        expectedSequence: userMsg.expectedSequence,
        websitePreviewContext: userMsg.websitePreviewContext,
        websitePreviewContextRef: userMsg.websitePreviewContextRef,
        canvasContext: userMsg.canvasContext,
        canvasContextRef: userMsg.canvasContextRef,
      });
      const steerReplayPromise = (async () => {
        for (const steer of turnSteers) {
          const result = await steerMessageInternal({
            content: steer.content,
            images: steer.images,
            documents: steer.documents,
            ...(steer.selectedSkills !== undefined ? { selectedSkills: steer.selectedSkills } : {}),
            targetSessionId: sessionId,
            ...(steer.targetModelId ? { targetModelId: steer.targetModelId } : {}),
            ...(steer.reasoningEffort !== undefined && steer.reasoningEffort !== null
              ? { reasoningEffort: steer.reasoningEffort }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(steer, 'activeReportId')
              ? { targetActiveReportId: steer.activeReportId }
              : {}),
            ...(steer.executionMode ? { executionMode: steer.executionMode } : {}),
            ...(steer.websitePreviewContextRef
              ? { websitePreviewContextRef: steer.websitePreviewContextRef }
              : {}),
            ...(steer.websitePreviewContext
              ? { websitePreviewContext: steer.websitePreviewContext }
              : {}),
            ...(steer.canvasContextRef ? { canvasContextRef: steer.canvasContextRef } : {}),
            ...(steer.canvasContext ? { canvasContext: steer.canvasContext } : {}),
            steerId: crypto.randomUUID(),
          });
          if (!result.accepted) break;
        }
      })();
      await Promise.all([retryPromise, steerReplayPromise]);
    },
    [messages, sendMessageInternal, sessionId, steerMessageInternal]
  );

  // Key handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessageRef.current();
    }
  }, []);

  const continueRun = useCallback(
    async (
      status: ResearchRunStatus,
      action: Exclude<ResearchRunAction, 'cancel'>,
      message: string
    ) => {
      if (!sessionId || isRuntimeSessionLoading(sessionId)) return;
      await sendMessageInternal({
        content: message,
        executionMode: status.mode,
        runId: status.runId,
        runAction: action,
        expectedSequence: status.sequence,
        targetActiveReportId: getActiveReportId?.() || undefined,
      });
    },
    [getActiveReportId, sendMessageInternal, sessionId]
  );

  const cancelRun = useCallback(
    async (status: ResearchRunStatus) => {
      if (!sessionId) return;
      const requestCancel = (expectedSequence: number) =>
        apiCall({
          action: 'cancel_run',
          sessionId,
          runId: status.runId,
          expectedSequence,
          turnId: `research-turn-${crypto.randomUUID()}`,
        });
      const applyRunStatus = (candidate: ResearchRunStatus) => {
        const next = setRuntimeRunStatus(sessionId, candidate) || candidate;
        setRunStatus(next);
        return next;
      };

      let res = await requestCancel(status.sequence);
      let data = await res.json();
      if (res.status === 409 && data.runStatus) {
        const latest = applyRunStatus(data.runStatus as ResearchRunStatus);
        const isNonterminal = !['completed', 'failed', 'cancelled'].includes(latest.phase);
        if (latest.runId === status.runId && latest.sequence !== status.sequence && isNonterminal) {
          res = await requestCancel(latest.sequence);
          data = await res.json();
        }
      }
      if (!res.ok) {
        if (res.status === 409) {
          if (data.runStatus) {
            applyRunStatus(data.runStatus as ResearchRunStatus);
            return;
          }
          const refreshed = await apiCall({ action: 'get_session', sessionId });
          const refreshedData = (await refreshed.json()) as SessionRecoveryResponse;
          const latest =
            refreshedData.runStatus?.runId === status.runId
              ? refreshedData.runStatus
              : [...(refreshedData.session?.messages || [])]
                  .reverse()
                  .find(message => message.runStatus?.runId === status.runId)?.runStatus;
          if (latest) {
            applyRunStatus(latest);
            return;
          }
        }
        throw new Error(data.error?.message || 'Unable to cancel run.');
      }
      if (data.runStatus) {
        applyRunStatus(data.runStatus as ResearchRunStatus);
      }
    },
    [apiCall, sessionId]
  );

  const sendSubagentControl = useCallback(
    async (agentId: string, decision?: 'approved' | 'rejected') => {
      const turnId = getRuntimeInFlightTurnId(sessionId);
      if (!sessionId || !turnId || !runtimeOwnerMatches) return;
      const row = turnSnapshotsRef.current.get(turnId)?.subagents.find(item => item.id === agentId);
      if (!row || !canCancelSubagent(row)) return;
      const request = decision
        ? subagentApprovalRequest(sessionId, turnId, row, decision)
        : subagentCancelRequest(sessionId, turnId, agentId);
      if (!request) return;
      const controlId = `${turnId}:${agentId}`;
      if (pendingSubagentControlsRef.current.has(controlId)) return;
      pendingSubagentControlsRef.current.add(controlId);
      try {
        const response = await apiCall(request);
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new Error(data?.error?.message || 'The agent action could not be applied.');
        if (data?.subagent && researchChatRuntimeStore.ownerKey === normalizedRuntimeOwnerKey) {
          const current = turnSnapshotsRef.current.get(turnId)?.subagents || [];
          applyTurnSnapshot(turnId, sessionId, { subagents: upsertSubagentStatus(current, data.subagent) });
        }
      } catch (error) {
        if (mountedRef.current && activeSessionIdRef.current === sessionId) {
          toast.error(error instanceof Error ? error.message : 'The agent action could not be applied.');
        }
      } finally {
        pendingSubagentControlsRef.current.delete(controlId);
      }
    },
    [apiCall, applyTurnSnapshot, normalizedRuntimeOwnerKey, runtimeOwnerMatches, sessionId]
  );
  const cancelSubagent = useCallback(
    (agentId: string) => sendSubagentControl(agentId),
    [sendSubagentControl]
  );
  const reviewSubagentApproval = useCallback(
    (agentId: string, decision: 'approved' | 'rejected') => sendSubagentControl(agentId, decision),
    [sendSubagentControl]
  );

  const activeTurn = runtimeOwnerMatches ? getRuntimeInFlightTurn(sessionId) : null;

  return {
    isLoading: runtimeOwnerMatches ? isLoading : false,
    streamingText: runtimeOwnerMatches ? streamingText : '',
    thinkingText: runtimeOwnerMatches ? thinkingText : '',
    responsePhase: runtimeOwnerMatches ? responsePhase : null,
    streamTimeline: runtimeOwnerMatches ? streamTimeline : [],
    activeTools: runtimeOwnerMatches ? activeTools : [],
    subagents: runtimeOwnerMatches ? subagents : [],
    runStatus: runtimeOwnerMatches ? runStatus : null,
    activeSessionIds: runtimeOwnerMatches ? activeSessionIds : [],
    unreadSessionIds: runtimeOwnerMatches ? unreadSessionIds : [],
    scheduledCommands: runtimeOwnerMatches ? scheduledCommands : [],
    activeTurnId: activeTurn ? getRuntimeInFlightTurnId(sessionId) || undefined : undefined,
    activeTurnSelectedSkills: activeTurn?.optimisticMessage.selectedSkills,
    activeTurnExecutionMode: activeTurn?.optimisticMessage.executionMode,
    activeTurnModelId:
      activeTurn?.optimisticMessage.targetModelId || activeTurn?.optimisticMessage.modelId,
    activeTurnReasoningEffort: activeTurn?.optimisticMessage.reasoningEffort,
    activeTurnActiveReportId: activeTurn?.optimisticMessage.activeReportId,
    latestAppliedSteerReceipt,
    inputMessage,
    setInputMessage,
    sendMessage,
    sendMessageDirect,
    steerMessageDirect,
    steerScheduledCommand,
    continueRun,
    cancelRun,
    cancelSubagent,
    reviewSubagentApproval,
    cancelScheduledCommand,
    clearScheduledCommands,
    stopCurrentResponse,
    retryMessage,
    handleKeyDown,
  };
}
